// ProfilePage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import useSWR from "swr";
import {
  Container, Grid, Card, CardContent, Typography, Box, Avatar, Button, Chip,
  Tab, Tabs, IconButton, Stack, TextField, Alert, Skeleton, Paper, Divider,
  CircularProgress, Snackbar, Fab, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  Badge, Tooltip
} from "@mui/material";

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';


import {
  Business as BusinessIcon, LocationOn as LocationIcon,
  Language as LanguageIcon, Star as StarIcon, StarBorder as StarBorderIcon, Edit as EditIcon, LockOutlined as LockOutlinedIcon,
  Person as PersonIcon, SaveAlt as SaveIcon, Cancel as CancelIcon, Phone as PhoneIcon,
  Work as WorkIcon, School as SchoolIcon, TrendingUp as TrendingUpIcon,
  Favorite as FavoriteIcon, Group as GroupIcon, Groups as GroupsIcon,
  ThumbUp as ThumbUpIcon, Add as AddIcon, CameraAlt as CameraIcon,
  ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon,
  RateReview as ReflectionIcon, Psychology as ReflectIcon,
  Download as DownloadIcon, DragIndicator as DragIcon,
  DriveFolderUpload as ImportIcon,
  Gavel as PatentIcon,
  AutoAwesome as AutoAwesomeIcon, UploadFile as UploadFileIcon, Public as PublicIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "shared/helpers/axios";
import { axiosService, fetcher } from "shared/helpers/axios";
import VerifiedBadge from "components/common/VerifiedBadge";
import FollowButton from "components/common/buttons/FollowButton";
import { stateUrl } from 'components/social/connections/connectionsApi';
import VerifyMembershipDialog from "./VerifyMembershipDialog";
import { AddReflectionDialog } from "./reflections/ReflectionComponents";
import ReflectButton from "./ReflectButton";
import {
  EducationDialog,
  WorkExperienceDialog,
  PublicationDialog,
  PatentDialog,
  ProjectDialog,
  RichTextField,
} from "./ProfileDialogs";
import MeetingScheduleDialog from "../../common/dialogs/MeetingScheduleDialog";
import InviteToConsortiumDialog from "../../common/dialogs/InviteToConsortiumDialog";
import SuggestEntityDialog from "../../common/dialogs/SuggestEntityDialog";
import MyStatsTab from "./MyStatsTab";
import ReflectionsTab from "./ReflectionsTab";
import WritingSummaryCard from "./WritingSummaryCard";
import TogetherCard from "./TogetherCard";
import ORGANIZATION_ROLES from "../../../shared/constants/organizationRoles";
import ORGANIZATION_TYPES from "../../../shared/constants/organizationTypes";
import ActionButtons from "./ActionButtons";
import ReportButton from "components/common/ReportButton";
import AddAffiliationDialog from "./AddAffiliationDialog";
import CVImportDialog from "./CVImportDialog";
import ProfileCompletenessCard from "./ProfileCompletenessCard";
import AIIdeasDialog from "components/collab/ai/AIIdeasDialog";
import { RingAvatar, HEADLINE_SX } from "./IdentityBits";
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';
import GuideTour, { guideTargets, useGuideTour } from 'components/common/guide/GuideTour';


// skills shown on the person's card in the Collaboration Hub (UserProfileSkill.FEATURED_MAX)
const FEATURED_SKILLS = 5;

/* ===== Styled bits (from Enhanced layout) ===== */
const CoverImage = styled(Box)(() => ({
  height: "90px",
  background: "linear-gradient(180deg, rgba(102, 126, 234, 0.1) 0%, rgba(251, 251, 251, 0.1) 100%)",
  position: "relative",
  overflow: "hidden",
  borderRadius: "16px 16px 0 0",
}));
const ProfileCard = styled(Card)(() => ({
  borderRadius: "16px",
  overflow: "visible",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  border: "1px solid rgba(0,0,0,0.08)",
}));
const StatsCard = styled(Card)(() => ({
  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  border: "1px solid rgba(255,255,255,0.2)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 25px rgba(0,0,0,0.1)" },
}));

const bgtheme = {
  backgroundColor: `linear-gradient(145deg,       
    #ebe8ee53  0%,  
    #ebeaea42 50%, 
    #ebeaea07  100%
)`  
}



/* ===== Bulk DOI import dialog ===== */
const DOI_REGEX = /\b(10\.\d{4,}(?:\.\d+)*\/[^\s"&'<>,;)\]]+)/gi;

function extractDois(text) {
  const found = new Set();
  let m;
  const rx = new RegExp(DOI_REGEX.source, 'gi');
  while ((m = rx.exec(text)) !== null) {
    found.add(m[1].replace(/[.,;)\]]+$/, '')); // strip trailing punctuation
  }
  return Array.from(found);
}

function crossrefTypeToPubType(type) {
  const map = {
    'journal-article': 'journal',
    'proceedings-article': 'conference',
    'book': 'book',
    'book-chapter': 'chapter',
    'posted-content': 'preprint',
  };
  return map[type] || 'other';
}

const BulkImportPublicationsDialog = ({ open, onClose, onImportComplete, existingDois = [] }) => {
  const [text, setText] = useState('');
  const [entries, setEntries] = useState([]);
  const [importing, setImporting] = useState(false);

  const normalDoi = (doi) => doi.toLowerCase().replace(/^https?:\/\/doi\.org\//i, '').trim();

  const existingSet = new Set((existingDois || []).map(normalDoi));

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const dois = extractDois(val);
    setEntries(dois.map(doi => ({
      doi,
      status: existingSet.has(normalDoi(doi)) ? 'duplicate' : 'pending',
      title: null,
    })));
  };

  const handleClose = () => {
    if (importing) return;
    setText('');
    setEntries([]);
    onClose();
  };

  const handleImport = async () => {
    if (!entries.length || importing) return;
    setImporting(true);
    const updated = [...entries];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done' || updated[i].status === 'duplicate') continue;
      updated[i] = { ...updated[i], status: 'fetching' };
      setEntries([...updated]);

      try {
        const res = await fetch(`https://api.crossref.org/works/${encodeURIComponent(updated[i].doi)}`);
        if (!res.ok) throw new Error(`CrossRef returned ${res.status}`);
        const { message: w } = await res.json();

        const pubData = {
          title: (w.title?.[0] || updated[i].doi).replace(/<[^>]+>/g, ''),
          authors: (w.author || [])
            .map(a => [a.family, a.given].filter(Boolean).join(', '))
            .join('; '),
          journal: w['container-title']?.[0] || '',
          year: w.published?.['date-parts']?.[0]?.[0] || null,
          volume: w.volume || '',
          issue: w.issue || '',
          pages: w.page || '',
          doi: w.DOI || updated[i].doi,
          url: w.URL || '',
          abstract: (w.abstract || '').replace(/<[^>]+>/g, ''),
          publication_type: crossrefTypeToPubType(w.type),
          citation_count: w['is-referenced-by-count'] ?? 0,
        };

        await axiosService.post('/publications/', pubData);
        updated[i] = { ...updated[i], status: 'done', title: pubData.title };
      } catch (e) {
        updated[i] = { ...updated[i], status: 'error', error: e.message };
      }

      setEntries([...updated]);
    }

    setImporting(false);
    await onImportComplete();
  };

  const doneCount  = entries.filter(e => e.status === 'done').length;
  const errorCount = entries.filter(e => e.status === 'error').length;
  const dupCount   = entries.filter(e => e.status === 'duplicate').length;
  const allSettled = entries.length > 0 && entries.every(e => ['done', 'error', 'duplicate'].includes(e.status));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle>Import Publications by DOI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste publication references — one per line. DOI numbers are extracted automatically and publications are imported.
        </Typography>
        <TextField
          multiline
          minRows={5}
          maxRows={10}
          fullWidth
          label="Paste references or DOIs"
          placeholder={
            'Smith J et al. Title. Journal. 2023. doi:10.1000/xyz123\nhttps://doi.org/10.1038/nature12345\n10.1016/j.cell.2022.01.001'
          }
          value={text}
          onChange={handleTextChange}
          disabled={importing}
        />

        {entries.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {entries.length} DOI{entries.length !== 1 ? 's' : ''} found
              {dupCount > 0 && ` (${dupCount} already in library)`}
              {(importing || allSettled) && ` — ${doneCount} imported, ${errorCount} failed`}
            </Typography>
            <Stack spacing={0.75}>
              {entries.map((entry, i) => (
                <Box key={entry.doi} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {entry.status === 'pending'   && <Chip size="small" label="Pending"    sx={{ minWidth: 80 }} />}
                  {entry.status === 'fetching'  && <CircularProgress size={16} sx={{ mx: 0.5, flexShrink: 0 }} />}
                  {entry.status === 'done'      && <Chip size="small" color="success" label="Imported"  sx={{ minWidth: 80 }} />}
                  {entry.status === 'error'     && <Chip size="small" color="error"   label="Failed"    sx={{ minWidth: 80 }} />}
                  {entry.status === 'duplicate' && <Chip size="small" color="warning" label="In Library" sx={{ minWidth: 80 }} />}
                  <Typography variant="body2" noWrap sx={{ flex: 1, color: entry.status === 'error' ? 'error.main' : 'text.primary' }}>
                    {entry.title || entry.doi}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {allSettled ? 'Close' : 'Cancel'}
        </Button>
        {!allSettled && (() => {
          const toImport = entries.filter(e => e.status !== 'duplicate').length;
          return (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!toImport || importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            >
              {importing
                ? `Importing ${doneCount + errorCount + 1} / ${toImport}…`
                : `Import ${toImport} Publication${toImport !== 1 ? 's' : ''}`}
            </Button>
          );
        })()}
      </DialogActions>
    </Dialog>
  );
};

/* ===== Unified page ===== */
// The profile guide, opened from the "?" next to the title on your own profile: what to fill in so that
// your profile is found, matched to calls and chosen for consortia. Steps point at data-guide elements;
// skills and interests fall back to the tab bar until they have entries.
const PROFILE_GUIDE_STEPS = [
  { label: 'First impression', title: 'Your profile card',
    description: 'A photo, a one-line headline and your location are what people see first in searches, suggestions and invitations. Profiles with a photo and headline are opened and followed far more often.',
    target: 'profile-card' },
  { label: 'Fast start', title: 'Fill it from your CV or ORCID',
    description: 'Import from CV or fill from ORCID / the web to add your experience, education, publications and skills in one go. You review everything before it is saved.',
    target: 'profile-import' },
  { label: 'Progress', title: 'See what is still missing',
    description: 'The completeness card lists what would help most next. Complete profiles show up higher in researcher searches and get matched to funding calls.',
    target: 'profile-completeness' },
  { label: 'Affiliation', title: 'Add and verify your organisation',
    description: 'A verified affiliation tells consortium coordinators who you are and which institution you bring; many calls require partners from specific countries or organisation types.',
    target: ['profile-organizations', 'profile-tabs'] },
  { label: 'Matching', title: 'Skills & expertise',
    description: 'Your skills decide which funding calls appear as relevant to you and which consortia are suggested to you. Coordinators search them when they look for partners with a missing expertise.',
    target: ['profile-skills', 'profile-tabs'] },
  { label: 'Matching', title: 'Research interests',
    description: 'Interests are linked to call topics: they bring you the right calls, the right Interests posts and suggestions of people working on the same themes.',
    target: ['profile-interests', 'profile-tabs'] },
  { label: 'Track record', title: 'Experience, publications and projects',
    description: 'Coordinators check these before inviting you: past projects and publications show you can deliver your part of a work package. Choose here what others can see.',
    target: 'profile-tabs' },
  { label: 'Next step', title: 'Join a consortium',
    description: 'With a complete profile, open Consortium in the sidebar to join one that is forming, or post on the Interests page that you are seeking a consortium or partners for a call.',
    target: 'nav-consortium' },
];
const PROFILE_GUIDE_TARGETS = guideTargets(PROFILE_GUIDE_STEPS);

const ProfilePage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  
  
  const { user: currentUser, refreshUserData } = useUser();
  const [activeTab, setActiveTab] = useState(0);

  // data
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // edits
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingStats, setEditingStats] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const theme = useTheme();

  // local form
  const [profileData, setProfileData] = useState({
    first_name: "", last_name: "", username: "", email: "", bio: "", position: "",
    city: "", country: null, location: "", website: "", department: "", contact_number: "", address: "",
    headline: "", // UserProfile field
    timezone: "", // UserProfile field  
    orcid: "", // UserProfile field
    google_scholar: "", // UserProfile field
    publications_count: 0, citations_count: 0, h_index: 0, funding_amount: 0,
    active_projects_count: 0, collaborations_count: 0
  });

  // Skills and research interests (from UserProfile)
  const [userSkills, setUserSkills] = useState([]);
  const [researchInterests, setResearchInterests] = useState([]);

  // Skills management - text input for adding skills
  const [newSkillInput, setNewSkillInput] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  
  // Research interests management - text input for adding interests
  const [newInterestInput, setNewInterestInput] = useState('');
  const [availableInterests, setAvailableInterests] = useState([]);
  const [loadingInterests, setLoadingInterests] = useState(false);

  // New profile sections data - now from database
  const [publications, setPublications] = useState([]);
  const [patents, setPatents] = useState([]);
  const [pastProjects, setPastProjects] = useState([]);
  const [education, setEducation] = useState([]);
  const [workExperience, setWorkExperience] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [resume, setResume] = useState("");

  // Loading states for each section
  const [loadingPublications, setLoadingPublications] = useState(false);
  const [loadingPatents, setLoadingPatents] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingEducation, setLoadingEducation] = useState(false);
  const [loadingExperience, setLoadingExperience] = useState(false);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [updatingCitations, setUpdatingCitations] = useState(false);

  // Editing states for new sections
  const [editingPublications, setEditingPublications] = useState(false);
  const [editingProjects, setEditingProjects] = useState(false);
  const [editingEducation, setEditingEducation] = useState(false);
  const [editingExperience, setEditingExperience] = useState(false);

  // Patent dialog state
  const [patentDialogOpen, setPatentDialogOpen] = useState(false);
  const [currentEditPatent, setCurrentEditPatent] = useState(null);
  const [savingPatent, setSavingPatent] = useState(false);
  const [editingResume, setEditingResume] = useState(false);
  
  // Dialog states
  const [educationDialogOpen, setEducationDialogOpen] = useState(false);
  const [workExperienceDialogOpen, setWorkExperienceDialogOpen] = useState(false);
  const [publicationDialogOpen, setPublicationDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [currentEditNetwork, setCurrentEditNetwork] = useState(null);
  const [currentEditItem, setCurrentEditItem] = useState(null);

  // Organizations tab states
  const [editingOrgDescription, setEditingOrgDescription] = useState(null); // org id currently being edited
  const [orgDescriptionDraft, setOrgDescriptionDraft] = useState("");
  // Add affiliation dialog states
  const [addAffiliationDialogOpen, setAddAffiliationDialogOpen] = useState(false);
  const [editAffiliationDialogOpen, setEditAffiliationDialogOpen] = useState(false);
  
  // Reflection dialog state
  const [reflectionDialogOpen, setReflectionDialogOpen] = useState(false);
  const [reflectionContext, setReflectionContext] = useState({
    contentType: '',
    objectId: ''
  });
  
  // Meeting dialog state
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [cvImportOpen, setCvImportOpen] = useState(false);
  const [publicImportOpen, setPublicImportOpen] = useState(false);   // ORCID / OpenAlex / web page
  const [completenessKey, setCompletenessKey] = useState(0);
  const [ideasOpen, setIdeasOpen] = useState(false);
  
  // Invite to consortium dialog state
  const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);
  
  // Suggest entity dialog state
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [editingAffiliation, setEditingAffiliation] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [loadingAllOrgs, setLoadingAllOrgs] = useState(false);
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [creatingNewOrg, setCreatingNewOrg] = useState(false);
  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    org_type: 'other',
    address: {
      line1: '',
      line2: '',
      city: '',
      state_region: '',
      postal_code: '',
    }
  });
  
  // Fetch countries for location dropdown
  const { data: countriesData } = useSWR('/consortium/countries/', fetcher);
  // Labs this person belongs to (shortcuts under My Organizations)
  const { data: labsData } = useSWR(user?.id ? `/consortium/labs/?member=${user.id}&page_size=50` : null, fetcher);
  const userLabs = labsData?.results || (Array.isArray(labsData) ? labsData : []);
  const countries = countriesData || [];
  
  // Verification dialog states
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [membershipToVerify, setMembershipToVerify] = useState(null);
  const [affiliationForm, setAffiliationForm] = useState({
    organization: null,
    role: 'researcher',
    department: null,
    start_date: new Date().toISOString().split('T')[0],
    is_primary: false,
    status: 'active'
  });

  // Fetch organization memberships for Organizations tab using useSWR
  const { data: myOrgsData, isLoading: loadingMyOrgs, mutate: mutateMyOrgs } = useSWR(
    user?.id ? `/consortium/organization-memberships/?user=${user.id}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Process memberships data and fetch full organization details
  const myOrganizations = React.useMemo(() => {
    if (!myOrgsData || !myOrgsData.results) return [];
    
    // Transform membership data to organization format expected by UI
    const organizations = myOrgsData.results.map(membership => ({
      id: membership.org_id,
      name: membership.org_name,
      org_type: membership.org_type,
      // Membership-specific data
      membership_id: membership.id,
      membership_role: membership.role,
      membership_department_id: membership.department_id,
      membership_department_name: membership.department_name,
      membership_start_date: membership.start_date,
      membership_is_primary: membership.is_primary,
      membership_status: membership.status,
      membership_created_at: membership.created,
      // Verification data
      membership_is_verified: membership.is_verified,
      membership_verified_by: membership.verified_by,
      membership_verified_at: membership.verified_at,
      membership_can_edit_org: membership.can_edit_org,
      // Placeholder data - will be fetched separately for description/infrastructure
      description: '',
      infrastructure: [],
      memberships_count: '—',
      departments_count: '—',
    }));
    
    // Sort by creation date in ascending order (oldest first)
    return organizations.sort((a, b) => {
      const dateA = new Date(a.membership_created_at);
      const dateB = new Date(b.membership_created_at);
      return dateA - dateB;
    });
  }, [myOrgsData]);

  // Fetch full organization details for each organization
  React.useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!myOrganizations || myOrganizations.length === 0) return;
      
      try {
        // Fetch details for each organization
        const detailsPromises = myOrganizations.map(org => 
          axiosService.get(`/consortium/organizations/${org.id}/`)
            .then(res => res.data)
            .catch(err => {
              console.error(`Failed to fetch details for org ${org.id}:`, err);
              return null;
            })
        );
        
        const orgDetails = await Promise.all(detailsPromises);
        
        // Update organizations with fetched details
        myOrganizations.forEach((org, index) => {
          const details = orgDetails[index];
          if (details) {
            org.description = details.description || '';
            org.infrastructure = details.infrastructure || [];
            org.memberships_count = details.memberships_count || org.memberships_count;
            org.departments_count = details.departments_count || org.departments_count;
          }
        });
      } catch (err) {
        console.error('Failed to fetch organization details:', err);
      }
    };

    fetchOrgDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOrganizations.length]);

  const refreshMyOrganizations = async () => {
    await mutateMyOrgs();
  };



  const startEditOrgDescription = (org) => {
    setEditingOrgDescription(org.id);
    setOrgDescriptionDraft(org.description || "");
  };

  const cancelEditOrgDescription = () => {
    setEditingOrgDescription(null);
    setOrgDescriptionDraft("");
  };

  const saveOrgDescription = async (orgId) => {
    try {
      setUpdating(true);
      await axiosService.patch(`/consortium/organizations/${orgId}/update-description/`, { description: orgDescriptionDraft });
      setSnackbar({ open: true, message: 'Organization description updated', severity: 'success' });
      setEditingOrgDescription(null);
      await refreshMyOrganizations();
    } catch (e) {
      console.error('Failed to update org description', e);
      setSnackbar({ open: true, message: 'Failed to update description', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // const addInfrastructure = async (orgId) => {
  //   if (!infraForm.name.trim()) {
  //     setSnackbar({ open: true, message: 'Infrastructure name required', severity: 'error' });
  //     return;
  //   }
  //   try {
  //     setUpdating(true);
  //     await axiosService.post(`/consortium/organizations/${orgId}/add-infrastructure/`, infraForm);
  //     setSnackbar({ open: true, message: 'Infrastructure added', severity: 'success' });
  //     setInfraForm({ name: "", description: "", category: "", availability: "" });
  //     await refreshMyOrganizations();
  //   } catch (e) {
  //     console.error('Failed to add infrastructure', e);
  //     setSnackbar({ open: true, message: 'Failed to add infrastructure', severity: 'error' });
  //   } finally {
  //     setUpdating(false);
  //   }
  // };

  // const removeInfrastructure = async (orgId, index) => {
  //   try {
  //     setUpdating(true);
  //     await axiosService.delete(`/consortium/organizations/${orgId}/remove-infrastructure/${index}/`);
  //     setSnackbar({ open: true, message: 'Infrastructure removed', severity: 'success' });
  //     await refreshMyOrganizations();
  //   } catch (e) {
  //     console.error('Failed to remove infrastructure', e);
  //     setSnackbar({ open: true, message: 'Failed to remove infrastructure', severity: 'error' });
  //   } finally {
  //     setUpdating(false);
  //   }
  // };

  // Affiliation management functions
  const fetchAllOrganizations = async () => {
    try {
      setLoadingAllOrgs(true);
      const response = await axiosService.get('/consortium/organizations/?page_size=500');
      setAllOrganizations(response.data.results || response.data || []);
    } catch (e) {
      console.error('Failed to fetch organizations', e);
      setSnackbar({ open: true, message: 'Failed to load organizations', severity: 'error' });
    } finally {
      setLoadingAllOrgs(false);
    }
  };

  const fetchOrgDepartments = async (orgId) => {
    try {
      setLoadingDepartments(true);
      const response = await axiosService.get(`/consortium/organizations/${orgId}/departments/`);
      setOrgDepartments(response.data.results || response.data || []);
    } catch (e) {
      console.error('Failed to fetch departments', e);
      setOrgDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const openAddAffiliationDialog = () => {
    fetchAllOrganizations();
    setAddAffiliationDialogOpen(true);
  };

  const closeAddAffiliationDialog = () => {
    setAddAffiliationDialogOpen(false);
    setOrgDepartments([]);
    setCreatingNewOrg(false);
    setNewOrgForm({
      name: '',
      org_type: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state_region: '',
        postal_code: '',
        country_code: '',
      }
    });
    setAffiliationForm({
      organization: null,
      role: '',
      department: null,
      start_date: new Date().toISOString().split('T')[0],
      is_primary: false,
      status: 'active'
    });
  };

  const handleAddAffiliation = async (membershipData) => {
    if (!membershipData) {
      setSnackbar({ open: true, message: 'Please select an organization or create a new one', severity: 'error' });
      return;
    }

    try {
      setUpdating(true);
      // Add the current user ID
      membershipData.user = currentUser.id;

      // If setting as primary and there's already a primary, clear it first
      if (membershipData.is_primary && myOrganizations.length > 0) {
        const currentPrimary = myOrganizations.find(m => m.membership_is_primary);
        if (currentPrimary) {
          await axiosService.patch(`/consortium/organization-memberships/${currentPrimary.membership_id}/`, {
            is_primary: false,
          });
        }
      }

      await axiosService.post('/consortium/organization-memberships/', membershipData);
      setSnackbar({ open: true, message: 'Affiliation added successfully', severity: 'success' });
      closeAddAffiliationDialog();
      await refreshMyOrganizations();
      await refreshUserData(); // TODO: fix the problem 
    } catch (e) {
      console.error('Failed to add affiliation', e);
      const errorMsg = e.response?.data ? JSON.stringify(e.response.data) : 'Failed to add affiliation';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const openEditAffiliationDialog = async (membership) => {
    setEditingAffiliation(membership);
    fetchAllOrganizations();
    
    // Pre-populate the form with existing data
    const orgObj = { id: membership.organization_id, name: membership.organization_name };
    const deptObj = membership.department_id ? { id: membership.department_id, name: membership.department_name } : null;
    
    setAffiliationForm({
      organization: orgObj,
      role: membership.role || 'researcher',
      department: deptObj,
      start_date: membership.start_date || new Date().toISOString().split('T')[0],
      is_primary: membership.is_primary || false,
      status: membership.status || 'active'
    });

    // Fetch departments for the organization
    if (membership.organization_id) {
      await fetchOrgDepartments(membership.organization_id);
    }
    
    setEditAffiliationDialogOpen(true);
  };

  const closeEditAffiliationDialog = () => {
    setEditAffiliationDialogOpen(false);
    setEditingAffiliation(null);
    setOrgDepartments([]);
    setAffiliationForm({
      organization: null,
      role: 'researcher',
      department: null,
      start_date: new Date().toISOString().split('T')[0],
      is_primary: false,
      status: 'active'
    });
  };

  // Education dialog functions
  const openEditEducationDialog = (education) => {
    setCurrentEditItem(education);
    setEducationDialogOpen(true);
  };

  // Work Experience dialog functions
  const openEditWorkExperienceDialog = (experience) => {
    setCurrentEditItem(experience);
    setWorkExperienceDialogOpen(true);
  };

  // Publication dialog functions
  const openEditPublicationDialog = (publication) => {
    setCurrentEditItem(publication);
    setPublicationDialogOpen(true);
  };

  // Project dialog functions
  const openEditProjectDialog = (project) => {
    setCurrentEditItem(project);
    setProjectDialogOpen(true);
  };

  const handleEditAffiliation = async () => {
    if (!editingAffiliation) return;

    try {
      setUpdating(true);
      const updateData = {
        role: affiliationForm.role,
        start_date: affiliationForm.start_date,
        is_primary: affiliationForm.is_primary,
        status: affiliationForm.status
      };

      // Handle department - can be existing (object with id) or new (string name)
      if (affiliationForm.department) {
        if (typeof affiliationForm.department === 'object' && affiliationForm.department.id) {
          updateData.department = affiliationForm.department.id;
        } else if (typeof affiliationForm.department === 'string' && affiliationForm.department.trim()) {
          updateData.department_name = affiliationForm.department.trim();
        }
      } else {
        // Explicitly set to null to remove department
        updateData.department = null;
      }

      await axiosService.patch(`/consortium/organization-memberships/${editingAffiliation.membership_id}/`, updateData);
      setSnackbar({ open: true, message: 'Affiliation updated successfully', severity: 'success' });
      closeEditAffiliationDialog();
      await refreshMyOrganizations();
    } catch (e) {
      console.error('Failed to update affiliation', e);
      const errorMsg = e.response?.data ? JSON.stringify(e.response.data) : 'Failed to update affiliation';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAffiliation = async (membershipId) => {
    if (!window.confirm('Are you sure you want to remove this affiliation?')) {
      return;
    }

    try {
      setUpdating(true);
      await axiosService.delete(`/consortium/organization-memberships/${membershipId}/`);
      setSnackbar({ open: true, message: 'Affiliation removed successfully', severity: 'success' });
      await refreshMyOrganizations();
    } catch (e) {
      console.error('Failed to delete affiliation', e);
      setSnackbar({ open: true, message: 'Failed to remove affiliation', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const openVerifyDialog = (membership) => {
    setMembershipToVerify(membership);
    setVerifyDialogOpen(true);
  };

  const closeVerifyDialog = () => {
    setVerifyDialogOpen(false);
    setMembershipToVerify(null);
  };

  const handleUploadVerificationDocument = async (membershipId, document) => {
    try {
      const formData = new FormData();
      formData.append('verification_document', document);

      await axiosService.post(
        `/consortium/organization-memberships/${membershipId}/upload-verification-document/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSnackbar({ 
        open: true, 
        message: 'Verification document uploaded successfully! An admin will review it shortly.', 
        severity: 'success' 
      });
      
      await refreshMyOrganizations();
      return Promise.resolve();
    } catch (e) {
      console.error('Failed to upload verification document', e);
      const errorMsg = e.response?.data?.error || 'Failed to verify membership. Please try again.';
      setSnackbar({ 
        open: true, 
        message: errorMsg, 
        severity: 'error' 
      });
      return Promise.reject(new Error(errorMsg));
    }
  };

  /* ---------- helpers ---------- */
  const isOwnProfile = user && currentUser && (
    user.id === currentUser.id ||
    profileId === "me" ||
    !profileId
  );

  const formatUserDataForForm = (u) => {
    // Helper to convert falsy values (including 'False' string from backend) to empty string
    const cleanString = (val) => {
      if (!val || val === 'False' || val === 'false' || val === 'None' || val === 'null') return '';
      return String(val);
    };
    
    return {
      first_name: cleanString(u.first_name),
      last_name: cleanString(u.last_name),
      username: cleanString(u.username),
      email: cleanString(u.email),
      bio: cleanString(u.bio),
      position: cleanString(u.position),
      city: cleanString(u.city),
      country: u.country || null,
      location: cleanString(u.location),
      website: cleanString(u.website),
      department: u.department || "",
      contact_number: cleanString(u.contact_number),
      address: cleanString(u.address),
      headline: cleanString(u.headline),
      timezone: cleanString(u.timezone),
      orcid: cleanString(u.orcid),
      google_scholar: cleanString(u.google_scholar),
      publications_count: u.publications_count ?? 0,
      citations_count: u.citations_count ?? 0,
      h_index: u.h_index ?? 0,
      funding_amount: u.funding_amount ?? 0,
      active_projects_count: u.active_projects_count ?? 0,
      collaborations_count: u.collaborations_count ?? 0,
    };
  };

  const computeDisplayName = (u) =>
    (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "");

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };

  /* ---------- data: fetch once per target ---------- */

// QUIET: refresh in place, without the loading state (no flash of the page)
const fetchUserProfile = useCallback(async (targetUserId, { quiet = false } = {}) => {
  try {
    if (!quiet) setLoading(true);
    setError(null);
    if (!targetUserId) { setLoading(false); return; }

    // For own profile use the SWR-cached currentUser directly (no extra network
    // request).  Always write the latest value so that SWR revalidations — which
    // may arrive with research_interests populated after the initial render —
    // flow through to the user state.
    if (currentUser && String(currentUser.id) === String(targetUserId)) {
      setUser(currentUser);
      setLoading(false);
      return;
    }

    // Use cookies for authentication
    const res = await axiosService.get(`/users/${targetUserId}/`);

    // console.log('fetchUserProfile response:', res);

    if (!res.data) {
      throw new Error(`GET ${res.status} ${res.statusText}`);
    }

    setUser(res?.data);
  } catch (e) {
    console.error('fetch profile error:', e);
    setError(e.message || 'Failed to load profile');

    // Fallback to currentUser if viewing own profile
    if (currentUser && String(currentUser.id) === String(targetUserId)) {
      setUser(currentUser);
    }
  } finally {
    setLoading(false);
  }
}, [currentUser]);



  // decide which ID to use for load
  useEffect(() => {
    const target =
      profileId === "me" && currentUser ? currentUser.id :
      !profileId && currentUser       ? currentUser.id :
      profileId;

    if (target) fetchUserProfile(target);
    else setLoading(false);
  }, [profileId, currentUser, fetchUserProfile]);

  // sync form with user
  useEffect(() => {
    if (user) {
      setProfileData(formatUserDataForForm(user));
      // Always overwrite so stale data from a previous profile view is cleared
      // and SWR revalidation updates (e.g. research_interests arriving late) are applied.
      setUserSkills(user.skills || []);
      setResearchInterests(user.research_interests || []);
      // Load nested profile data from API response
      if (user.resume) setResume(user.resume);
      if (user.education) setEducation(user.education);
      if (user.work_experience) setWorkExperience(user.work_experience);
      if (user.networks) setNetworks(user.networks);
      if (user.publications) setPublications(user.publications);
      if (user.patents) setPatents(user.patents);
      else fetchPatents(user.profile);  // fetch separately if not embedded
      if (user.projects) setPastProjects(user.projects);
    }
  }, [user?.id]);

  // Fetch available skills for autocomplete
  useEffect(() => {
    const fetchAvailableSkills = async () => {
      setLoadingSkills(true);
      try {
        const response = await axiosService.get('/skills/');
        setAvailableSkills(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching available skills:', err);
        setAvailableSkills([]);
      } finally {
        setLoadingSkills(false);
      }
    };
    fetchAvailableSkills();
  }, []);

  // Fetch available call categories for autocomplete (research interests are now linked to call categories)
  useEffect(() => {
    const fetchAvailableInterests = async () => {
      setLoadingInterests(true);
      try {
        const response = await axiosService.get('/funding/call-categories/');
        setAvailableInterests(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching available call categories:', err);
        setAvailableInterests([]);
      } finally {
        setLoadingInterests(false);
      }
    };
    fetchAvailableInterests();
  }, []);

  /* ---------- Data fetching helpers ---------- */

  const fetchEducation = async () => {
    try {
      setLoadingEducation(true);
      const response = await axiosService.get(`/education/`);
      setEducation(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching education:', err);
    } finally {
      setLoadingEducation(false);
    }
  };

  const fetchWorkExperience = async () => {
    try {
      setLoadingExperience(true);
      const response = await axiosService.get('/work-experience/');
      setWorkExperience(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching work experience:', err);
    } finally {
      setLoadingExperience(false);
    }
  };

  const fetchNetworks = async () => {
    try {
      setLoadingNetworks(true);
      const response = await axiosService.get('/networks/');
      setNetworks(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching networks:', err);
    } finally {
      setLoadingNetworks(false);
    }
  };

  const fetchPublications = async () => {
    try {
      setLoadingPublications(true);
      const response = await axiosService.get('/publications/');
      setPublications(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching publications:', err);
    } finally {
      setLoadingPublications(false);
    }
  };

  const fetchPatents = async (profileId) => {
    try {
      setLoadingPatents(true);
      const params = profileId ? { profile: profileId } : {};
      const response = await axiosService.get('/patents/', { params });
      setPatents(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching patents:', err);
    } finally {
      setLoadingPatents(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await axiosService.get('/projects/');
      setPastProjects(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  /* ---------- AI CV import ---------- */

  const handleCVApplied = async (res) => {
    setCompletenessKey((k) => k + 1);
    setUserSkills(res.skills || []);
    setResearchInterests(res.research_interests || []);
    fetchEducation();
    fetchWorkExperience();
    fetchPublications();
    fetchPatents(user?.profile);
    fetchProjects();
    const fresh = await refreshUserData();
    if (fresh) {
      setUser(fresh);
      setProfileData(formatUserDataForForm(fresh));
      if (fresh.resume) setResume(fresh.resume);
    }
    const added = Object.values(res.created || {}).reduce((a, b) => a + b, 0);
    setSnackbar({
      open: true,
      message: added ? `Added ${added} item${added === 1 ? '' : 's'} from your CV.` : 'Nothing new to add — your profile already had these entries.',
      severity: 'success',
    });
  };

  /* ---------- save handlers (Modern behavior) ---------- */

const updateUserProfile = async (updateData) => {
  try {
    setUpdating(true);
    setError(null);

    const pathId = user?.id;
    const res = await axiosService.patch(`/users/${pathId}/`, updateData);

    if (!res.data) {
      // const text = await res.statusText;
      throw new Error(`PATCH ${res.status} ${res.statusText}`);
    }

    const data = res?.data || await res.json();
    // Merge instead of replace so fields absent from the PATCH response don't
    // wipe out data already held in state (e.g. research_interests, bio).
    setUser(prev => ({ ...prev, ...data }));
    if (isOwnProfile) {
      // Refresh the global user context
      await refreshUserData();
    }
    setSnackbar({ open: true, message: 'Profile updated', severity: 'success' });
    return data;
  } catch (e) {
    const msg = e.message || 'Failed to update profile';
    setError(msg);
    setSnackbar({ open: true, message: msg, severity: 'error' });
    throw e;
  } finally {
    setUpdating(false);
  }
};



  const handleEditProfile = () => setEditingProfile(true);

  // Auto-save individual field on blur
  const handleFieldBlur = async (field, value) => {
    // Skip if value hasn't changed
    if (user && user[field] === value) return;
    
    try {
      const updateData = { [field]: value };
      
      // Handle special cases
      if (field === 'country' && value) {
        updateData.country_code = value.code;
        delete updateData.country;
      }
      
      await updateUserProfile(updateData);
    } catch (e) {
      console.error(`Failed to auto-save ${field}:`, e);
    }
  };

  const processAvatarFile = (file) => new Promise((resolve, reject) => {
    const AVATAR_SIZE = 512;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const minDim = Math.min(img.naturalWidth, img.naturalHeight);
      // Never upscale — cap at the original square dimension
      const size = Math.min(AVATAR_SIZE, minDim);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      const sx = (img.naturalWidth  - minDim) / 2;
      const sy = (img.naturalHeight - minDim) / 2;
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
      canvas.toBlob(
        (blob) => blob
          ? resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
          : reject(new Error('Canvas conversion failed')),
        'image/jpeg',
        0.85,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Could not load image')); };
    img.src = objectUrl;
  });

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      return;
    }

    // 10 MB raw limit — backend also enforces this
    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Image must be smaller than 10 MB', severity: 'error' });
      return;
    }

    try {
      setUploadingAvatar(true);

      // Resize + crop to 512×512 JPEG on the client before uploading
      const processed = await processAvatarFile(file);

      const formData = new FormData();
      formData.append('avatar', processed);

      const res = await axiosService.patch(`/users/${user?.id}/`, formData);

      

      if (!res.data) {
        // const text = await res.statusText;
        throw new Error(`Failed to upload avatar: ${res.statusText}`);
      }

      const data = res?.data || await res.json() ;
      setUser(prev => ({ ...prev, ...data }));
      if (isOwnProfile) {
        // Refresh the global user context
        await refreshUserData();
      }
      setSnackbar({ open: true, message: 'Avatar updated successfully', severity: 'success' });
    } catch (e) {
      console.error('Avatar upload error:', e);
      setSnackbar({ open: true, message: e.message || 'Failed to upload avatar', severity: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploadingAvatar(true);
      const res = await axiosService.post(`/users/${user?.id}/remove-avatar/`);
      setUser(prev => ({ ...prev, ...res.data }));
      if (isOwnProfile) await refreshUserData();
      setSnackbar({ open: true, message: 'Profile photo removed', severity: 'success' });
    } catch (e) {
      console.error('Remove avatar error:', e);
      setSnackbar({ open: true, message: 'Failed to remove profile photo', severity: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    if (editingProfile) {
      const invalidField = PROFILE_TEXT_FIELDS.find(f => isIntegerOnly(profileData[f]));
      if (invalidField) {
        alert(`"${invalidField.replace('_', ' ')}" cannot be a number only.`);
        return;
      }
      const updateData = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        bio: profileData.bio,
        position: profileData.position,
        city: profileData.city,
        country_code: profileData.country?.code || null,
        website: profileData.website,
        department: profileData.department,
        contact_number: profileData.contact_number,
        address: profileData.address,
        headline: profileData.headline,
        timezone: profileData.timezone,
        orcid: profileData.orcid,
        google_scholar: profileData.google_scholar,
      };
      await updateUserProfile(updateData);
    }
    setEditingProfile(false);
    setEditingResume(false);
  };

  const handleSaveStats = async () => {
    const stats = {
      publications_count: Number(profileData.publications_count) || 0,
      citations_count: Number(profileData.citations_count) || 0,
      h_index: Number(profileData.h_index) || 0,
      funding_amount: Number(profileData.funding_amount) || 0,
      active_projects_count: Number(profileData.active_projects_count) || 0,
      collaborations_count: Number(profileData.collaborations_count) || 0,
    };
    await updateUserProfile(stats);
    setEditingStats(false);
  };

  const handleCancelEdit = () => {
    if (user) setProfileData(formatUserDataForForm(user));
    setEditingProfile(false);
    setEditingStats(false);
    setEditingPublications(false);
    setEditingProjects(false);
    setEditingEducation(false);
    setEditingExperience(false);
    setEditingResume(false);
  };

  const PROFILE_TEXT_FIELDS = ['first_name', 'last_name', 'position', 'headline', 'bio', 'city', 'address', 'orcid', 'google_scholar', 'website'];
  const isIntegerOnly = (val) => typeof val === 'string' && /^\d+$/.test(val.trim());

  const handleInputChange = (field, value) => {
    if (PROFILE_TEXT_FIELDS.includes(field) && isIntegerOnly(value)) return;
    setProfileData((p) => ({ ...p, [field]: value }));
  };

  // Handle skill management
  const handleAddSkill = async () => {
    // newSkillInput can be a string (free text) or an object from Autocomplete
    const skillName = typeof newSkillInput === 'string' ? newSkillInput.trim() : newSkillInput?.name;
    if (!skillName) return;
    
    try {
      setUpdating(true);
      const response = await axiosService.post('/user-skills/', {
        skill_name: skillName,
        proficiency: 5,
        years_exp: 1
      });
      
      setUserSkills([...userSkills, response.data]);
      setNewSkillInput('');
      setSnackbar({ open: true, message: 'Skill added successfully', severity: 'success' });
    } catch (err) {
      console.error('Error adding skill:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to add skill';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // up to FEATURED_SKILLS skills picked for the person's card in the Collaboration Hub
  const handleToggleFeaturedSkill = async (userSkill) => {
    const next = !userSkill.featured;
    if (next && userSkills.filter((s) => s.featured).length >= FEATURED_SKILLS) {
      setSnackbar({ open: true, message: `You can show at most ${FEATURED_SKILLS} skills on your card. Unpick one first.`, severity: 'info' });
      return;
    }
    setUserSkills((list) => list.map((s) => (s.id === userSkill.id ? { ...s, featured: next } : s)));
    try {
      await axiosService.patch(`/user-skills/${userSkill.id}/`, { featured: next });
    } catch (err) {
      setUserSkills((list) => list.map((s) => (s.id === userSkill.id ? { ...s, featured: !next } : s)));
      setSnackbar({ open: true, message: err.response?.data?.featured?.[0] || 'The skill could not be updated', severity: 'error' });
    }
  };

  const handleDeleteSkill = async (skillId) => {
    try {
      setUpdating(true);
      await axiosService.delete(`/user-skills/${skillId}/`);
      setUserSkills(userSkills.filter(s => s.id !== skillId));
      setSnackbar({ open: true, message: 'Skill removed', severity: 'success' });
    } catch (err) {
      console.error('Error deleting skill:', err);
      setSnackbar({ open: true, message: 'Failed to remove skill', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // Handle research interest management
  const handleAddInterest = async () => {
    // newInterestInput can be a string (free text) or an object from Autocomplete
    const interestName = typeof newInterestInput === 'string' ? newInterestInput.trim() : newInterestInput?.name;
    if (!interestName) return;
    
    try {
      setUpdating(true);
      const response = await axiosService.post('/user-research-interests/', {
        area_name: interestName,
        weight: 5
      });
      
      setResearchInterests([...researchInterests, response.data]);
      setNewInterestInput('');
      setSnackbar({ open: true, message: 'Research interest added successfully', severity: 'success' });
    } catch (err) {
      console.error('Error adding interest:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to add research interest';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteInterest = async (interestId) => {
    try {
      setUpdating(true);
      await axiosService.delete(`/user-research-interests/${interestId}/`);
      setResearchInterests(researchInterests.filter(i => i.id !== interestId));
      setSnackbar({ open: true, message: 'Research interest removed', severity: 'success' });
    } catch (err) {
      console.error('Error deleting interest:', err);
      setSnackbar({ open: true, message: 'Failed to remove research interest', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };
  
  const handleSaveResume = async () => {
    try {
      setUpdating(true);
      await axiosService.patch(`/user-profiles/me/`, { resume });
      setSnackbar({ open: true, message: 'Resume updated successfully', severity: 'success' });
      setEditingResume(false);
    } catch (err) {
      console.error('Error saving resume:', err);
      setSnackbar({ open: true, message: 'Failed to save resume', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  /* ---------- Education handlers ---------- */
  
  const handleSaveEducation = async (educationData) => {
    try {
      setUpdating(true);
      if (educationData.id) {
        // Update existing
        await axiosService.patch(`/education/${educationData.id}/`, educationData);
      } else {
        // Create new
        await axiosService.post('/education/', educationData);
      }
      await fetchEducation();
      setSnackbar({ open: true, message: 'Education updated successfully', severity: 'success' });
      setEditingEducation(false);
      setEducationDialogOpen(false);
      setCurrentEditItem(null);
    } catch (err) {
      console.error('Error saving education:', err);
      const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      setSnackbar({ open: true, message: `Failed to save education: ${errorMsg}`, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteEducation = async (id) => {
    try {
      await axiosService.delete(`/education/${id}/`);
      await fetchEducation();
      setSnackbar({ open: true, message: 'Education deleted', severity: 'success' });
    } catch (err) {
      console.error('Error deleting education:', err);
      setSnackbar({ open: true, message: 'Failed to delete education', severity: 'error' });
    }
  };

  /* ---------- Work Experience handlers ---------- */
  
  const handleSaveWorkExperience = async (experienceData) => {
    try {
      setUpdating(true);
      if (experienceData.id) {
        await axiosService.patch(`/work-experience/${experienceData.id}/`, experienceData);
      } else {
        await axiosService.post('/work-experience/', experienceData);
      }
      await fetchWorkExperience();
      setSnackbar({ open: true, message: 'Work experience updated successfully', severity: 'success' });
      setEditingExperience(false);
      setWorkExperienceDialogOpen(false);
      setCurrentEditItem(null);
    } catch (err) {
      console.error('Error saving work experience:', err);
      const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      setSnackbar({ open: true, message: `Failed to save work experience: ${errorMsg}`, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteWorkExperience = async (id) => {
    try {
      await axiosService.delete(`/work-experience/${id}/`);
      await fetchWorkExperience();
      setSnackbar({ open: true, message: 'Work experience deleted', severity: 'success' });
    } catch (err) {
      console.error('Error deleting work experience:', err);
      setSnackbar({ open: true, message: 'Failed to delete work experience', severity: 'error' });
    }
  };

  /* ---------- Network handlers ---------- */

  const handleSaveNetwork = async (data) => {
    try {
      setUpdating(true);
      if (data.id) {
        await axiosService.patch(`/networks/${data.id}/`, data);
      } else {
        await axiosService.post('/networks/', data);
      }
      await fetchNetworks();
      setSnackbar({ open: true, message: 'Network saved successfully', severity: 'success' });
      setNetworkDialogOpen(false);
      setCurrentEditNetwork(null);
    } catch (err) {
      const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      setSnackbar({ open: true, message: `Failed to save network: ${errorMsg}`, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteNetwork = async (id) => {
    try {
      await axiosService.delete(`/networks/${id}/`);
      await fetchNetworks();
      setSnackbar({ open: true, message: 'Network removed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove network', severity: 'error' });
    }
  };

  /* ---------- Publication handlers ---------- */
  
  const handleSavePublication = async (publicationData) => {
    try {
      setUpdating(true);
      if (publicationData.id) {
        await axiosService.patch(`/publications/${publicationData.id}/`, publicationData);
      } else {
        await axiosService.post('/publications/', publicationData);
      }
      await fetchPublications();
      setSnackbar({ open: true, message: 'Publication updated successfully', severity: 'success' });
      setPublicationDialogOpen(false);
      setEditingPublications(false);
    } catch (err) {
      console.error('Error saving publication:', err);
      const errorMessage = err.response?.data 
        ? `Failed to save publication: ${JSON.stringify(err.response.data)}`
        : 'Failed to save publication';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePublication = async (id) => {
    try {
      await axiosService.delete(`/publications/${id}/`);
      await fetchPublications();
      setSnackbar({ open: true, message: 'Publication deleted', severity: 'success' });
    } catch (err) {
      console.error('Error deleting publication:', err);
      setSnackbar({ open: true, message: 'Failed to delete publication', severity: 'error' });
    }
  };

  const handleUpdateCitations = async () => {
    if (!user) return;
    
    try {
      setUpdatingCitations(true);
      
      // Determine identifier to use (prefer ORCID, fallback to name)
      let identifier_type = 'name';
      let identifier_value = `${user.first_name} ${user.last_name}`.trim();
      
      if (user.orcid && user.orcid !== 'False' && user.orcid !== 'false') {
        identifier_type = 'orcid';
        // Normalize: strip URL prefix (e.g. "https://orcid.org/") so only the bare ID is sent
        identifier_value = user.orcid.replace(/^https?:\/\/orcid\.org\//i, '').trim();
      }
      
      const response = await axiosService.post('/publications/update-citations/', {
        identifier_type,
        identifier_value,
      });
      
      const data = response.data;
      setSnackbar({ 
        open: true, 
        message: `${data.message}${data.warnings ? ' (Some publications could not be matched)' : ''}`, 
        severity: 'success' 
      });
      
      // Refresh publications to show updated citation counts
      await fetchPublications();
    } catch (e) {
      console.error('Failed to update citations', e);
      const errorMsg = e.response?.data?.error || 'Failed to update citation counts';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setUpdatingCitations(false);
    }
  };

  const handleReorderPublication = async (publicationId, direction) => {
    try {
      await axiosService.post(`/publications/${publicationId}/reorder/`, {
        direction: direction // 'up' or 'down'
      });
      
      // Refresh publications to show new order
      await fetchPublications();
    } catch (e) {
      console.error('Failed to reorder publication', e);
      const errorMsg = e.response?.data?.error || 'Failed to reorder publication';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleDragEndPublication = async (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Optimistically update the UI
    const reorderedPubs = Array.from(publications);
    const [movedPub] = reorderedPubs.splice(sourceIndex, 1);
    reorderedPubs.splice(destIndex, 0, movedPub);
    setPublications(reorderedPubs);
    
    try {
      // Create the new order array with updated display_order values
      const publication_order = reorderedPubs.map((pub, index) => ({
        id: pub.id,
        display_order: index + 1
      }));
      
      await axiosService.post('/publications/bulk-reorder/', {
        publication_order
      });
      
      setSnackbar({ open: true, message: 'Publications reordered', severity: 'success' });
    } catch (e) {
      console.error('Failed to reorder publications', e);
      // Revert on error
      await fetchPublications();
      const errorMsg = e.response?.data?.error || 'Failed to reorder publications';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleDragEndPatent = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const reordered = Array.from(patents);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setPatents(reordered);
    try {
      await axiosService.post('/patents/bulk-reorder/', {
        patent_order: reordered.map((p, i) => ({ id: p.id, display_order: i + 1 })),
      });
      setSnackbar({ open: true, message: 'Patents reordered', severity: 'success' });
    } catch (e) {
      await fetchPatents();
      setSnackbar({ open: true, message: e.response?.data?.error || 'Failed to reorder patents', severity: 'error' });
    }
  };

  const handleDragEndProject = async (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const reordered = Array.from(pastProjects);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setPastProjects(reordered);
    try {
      await axiosService.post('/projects/bulk-reorder/', {
        project_order: reordered.map((p, i) => ({ id: p.id, display_order: i + 1 })),
      });
      setSnackbar({ open: true, message: 'Projects reordered', severity: 'success' });
    } catch (e) {
      await fetchProjects();
      setSnackbar({ open: true, message: e.response?.data?.error || 'Failed to reorder projects', severity: 'error' });
    }
  };

  /* ---------- Reflection handlers ---------- */
  
  const handleOpenReflectionDialog = (contentType, objectId) => {
    setReflectionContext({
      contentType,
      objectId
    });
    setReflectionDialogOpen(true);
  };

  const handleReflect = (contentType, objectId) => {
    // The Reflections page, filtered to this item's reflections
    navigate(`/profile/${user?.id}/reflections/?item=${contentType}:${objectId}`);
  };

  const handleCloseReflectionDialog = (success) => {
    setReflectionDialogOpen(false);
    if (success) {
      setSnackbar({ open: true, message: 'Reflection submitted successfully', severity: 'success' });
    }
  };

  /* ---------- Patent handlers ---------- */

  const handleSavePatent = async (patentData) => {
    try {
      setSavingPatent(true);
      if (patentData.id) {
        await axiosService.patch(`/patents/${patentData.id}/`, patentData);
      } else {
        await axiosService.post('/patents/', patentData);
      }
      await fetchPatents();
      setSnackbar({ open: true, message: 'Patent saved successfully', severity: 'success' });
      setPatentDialogOpen(false);
      setCurrentEditPatent(null);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save patent', severity: 'error' });
    } finally {
      setSavingPatent(false);
    }
  };

  const handleDeletePatent = async (id) => {
    try {
      await axiosService.delete(`/patents/${id}/`);
      await fetchPatents();
      setSnackbar({ open: true, message: 'Patent deleted', severity: 'success' });
    } catch (_) {
      setSnackbar({ open: true, message: 'Failed to delete patent', severity: 'error' });
    }
  };

  /* ---------- Project handlers ---------- */

  const handleSaveProject = async (projectData) => {
    try {
      setUpdating(true);
      if (projectData.id) {
        await axiosService.patch(`/projects/${projectData.id}/`, projectData);
      } else {
        await axiosService.post('/projects/', projectData);
      }
      await fetchProjects();
      setSnackbar({ open: true, message: 'Project updated successfully', severity: 'success' });
      setProjectDialogOpen(false);
      setEditingProjects(false);
    } catch (err) {
      console.error('Error saving project:', err);
      const errorMessage = err.response?.data 
        ? `Failed to save project: ${JSON.stringify(err.response.data)}`
        : 'Failed to save project';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await axiosService.delete(`/projects/${id}/`);
      await fetchProjects();
      setSnackbar({ open: true, message: 'Project deleted', severity: 'success' });
    } catch (err) {
      console.error('Error deleting project:', err);
      setSnackbar({ open: true, message: 'Failed to delete project', severity: 'error' });
    }
  };

  /* ---------- Resume Download ---------- */
  const handleDownloadResume = async () => {
    try {
      // Dynamically import html2pdf
      const html2pdf = (await import('html2pdf.js')).default;

      // Everything typed by the user goes into HTML: escape it so '<', '&' or quotes can't break the layout
      const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const blank = (v) => !v || v === 'False' || v === 'false';
      // One date style everywhere: "Mar 2021"; a bare year stays a year
      const fmtDate = (d) => {
        if (!d) return '';
        if (/^\d{4}$/.test(String(d))) return String(d);
        const t = new Date(d);
        return Number.isNaN(t.getTime()) ? esc(d) : t.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      const span = (start, end, current) => {
        const a = fmtDate(start), b = current ? 'present' : (fmtDate(end) || (a ? 'present' : ''));
        return a || b ? `${a}${a && b ? ' – ' : ''}${b}` : '';
      };
      const httpUrl = (u) => (/^https?:\/\//i.test(String(u || '')) ? String(u) : '');

      // The photo is embedded as data: html2canvas can't paint an image from the media host (CORS)
      let photo = null;
      if (user?.avatar && isOwnProfile) {
        try { photo = (await axiosService.get('/user-profiles/me/avatar-data/')).data?.data_url || null; } catch { photo = null; }
      }

      const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'User';
      const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const skills = (user?.skills || []).map((x) => x?.skill?.name || x?.name).filter(Boolean);
      const interests = (user?.research_interests || []).map((x) => x?.category?.name || x?.name).filter(Boolean);
      const metrics = [
        user?.h_index != null && ['h-index', user.h_index],
        user?.citations_count != null && ['Citations', Number(user.citations_count).toLocaleString('en-US')],
        user?.publications_count != null && ['Publications', user.publications_count],
      ].filter(Boolean);
      const sortedPubs = [...(publications || [])].sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0));
      
      // Generate HTML content with the provided layout
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    /* ── Reset ── */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* ── Page ── */
    @page {
      size: 21cm 29.7cm;
      margin: 0;
    }
    body {
      background: #fff;
      width: 21cm;
      font-family: 'Calibri', 'Gill Sans MT', Arial, sans-serif;
      font-size: 9pt;
      color: #1a1a1a;
    }
    page[size="A4"] {
      display: block;
      width: 21cm;
      min-height: 29.6cm;
      background: #fff;
    }

    /* ── Two-column wrapper ── */
    .container {
      display: flex;
      flex-direction: row;
      width: 100%;
      min-height: 29.6cm;   /* a hair under A4, so a one-page CV never spills onto a blank page */
    }

    /* ── LEFT PANEL ── */
    .leftPanel {
      width: 31%;
      background-color: #f4f5f6;
      border-right: 0.04cm solid #d0d4d8;
      padding: 0.6cm 0.45cm;
      display: flex;
      flex-direction: column;
      align-items: center;
      color: #1a1a1a;
    }
    .leftPanel a {
      color: #003677;
      text-decoration: none;
      word-break: break-all;
    }

    /* ── Profile photo ── */
    .profilePhoto {
      text-align: center;
      margin-bottom: 0.35cm;
    }
    .profilePhoto img {
      width: 3.2cm;
      height: 3.2cm;
      border-radius: 50%;
      border: 0.1cm solid #c0c6cc;
      object-fit: cover;
      object-position: center top;
    }

    /* ── Left panel details ── */
    .leftPanel .details {
      width: 100%;
    }
    .leftPanel .item {
      padding-top: 0.25cm;
      padding-bottom: 0.25cm;
      border-bottom: 0.04cm solid #c8cdd2;
    }
    .leftPanel .item:last-child {
      border-bottom: none;
    }

    /* ── Section headings ── */
    h2 {
      font-family: 'Calibri', 'Gill Sans MT', Arial, sans-serif;
      font-size: 8.5pt;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #003677;
      margin-bottom: 0.12cm;
      padding-bottom: 0.06cm;
      border-bottom: 0.03cm solid #003677;
    }

    /* ── Small text (body copy) ── */
    .smallText,
    .smallText span,
    .smallText p,
    .smallText a {
      font-size: 8pt;
      line-height: 1.45;
      color: #1a1a1a;
    }
    .smallText p { margin-top: 0.06cm; }
    .leftPanel .smallText,
    .leftPanel .smallText span,
    .leftPanel .smallText p {
      font-size: 7.5pt;
    }

    /* ── Contact icons ── */
    .contactIcon {
      display: inline-block;
      width: 0.4cm;
      font-size: 7.5pt;
    }

    /* ── Education entries (no box, just clean text) ── */
    .eduEntry {
      margin-bottom: 0.2cm;
    }
    .eduDegree {
      font-weight: 700;
      font-size: 7.5pt;
      color: #1a1a1a;
    }
    .eduInstitution {
      font-size: 7pt;
      color: #555;
      margin-top: 0.03cm;
    }
    .eduDates {
      font-size: 6.5pt;
      color: #777;
      font-style: italic;
    }

    /* ── RIGHT PANEL ── */
    .rightPanel {
      width: 69%;
      padding: 0.65cm 0.7cm;
    }

    /* ── Name & title block ── */
    .nameBlock {
      border-bottom: 0.06cm solid #003677;
      padding-bottom: 0.2cm;
      margin-bottom: 0.3cm;
    }
    h1 {
      font-family: 'Calibri', 'Gill Sans MT', Arial, sans-serif;
      font-size: 20pt;
      font-weight: 700;
      color: #003677;
      letter-spacing: 0.04em;
      line-height: 1.1;
      text-transform: uppercase;
    }
    h3 {
      font-size: 9.5pt;
      font-weight: 400;
      color: #555;
      margin-top: 0.08cm;
      font-style: italic;
    }

    /* ── Right panel sections ── */
    .rightPanel .section {
      margin-bottom: 0.35cm;
    }

    /* ── Timeline (work experience / projects) ── */
    .timeline {
      list-style: none;
      padding: 0;
    }
    .timeline li {
      position: relative;
      padding-left: 0.45cm;
      padding-bottom: 0.3cm;
    }
    .timeline li::before {
      content: '';
      position: absolute;
      left: 0.07cm;
      top: 0.18cm;
      bottom: -0.1cm;
      width: 0.04cm;
      background: #c0c6cc;
    }
    .timeline li:last-child::before { display: none; }
    .timeline li::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0.07cm;
      width: 0.28cm;
      height: 0.28cm;
      border-radius: 50%;
      background: #003677;
    }

    .jobRow {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }
    .jobTitle { font-weight: 700; font-size: 8.5pt; }
    .jobDates { font-size: 7.5pt; color: #777; white-space: nowrap; padding-left: 0.2cm; }
    .jobOrg   { font-size: 7.5pt; color: #555; margin-top: 0.03cm; font-style: italic; }
    .jobDesc  { font-size: 7.5pt; color: #333; margin-top: 0.05cm; text-align: justify; white-space: pre-wrap; }

    /* ── Publications ── */
    .pubEntry {
      padding-left: 0.3cm;
      border-left: 0.04cm solid #003677;
      margin-bottom: 0.2cm;
    }
    .pubTitle { font-weight: 700; font-size: 8pt; color: #1a1a1a; }
    .pubMeta  { font-size: 7pt; color: #555; margin-top: 0.03cm; }

    /* ── Chips (skills, interests) ── */
    .chips { display: flex; flex-wrap: wrap; gap: 0.08cm; }
    .chip { font-size: 6.8pt; padding: 0.03cm 0.16cm; border-radius: 0.3cm; background: #e4e9f0; color: #1f3a5f; }

    /* ── Metrics ── */
    .metrics { display: flex; justify-content: space-between; }
    .metric { text-align: center; flex: 1; }
    .metricValue { font-size: 11pt; font-weight: 700; color: #003677; line-height: 1.1; }
    .metricLabel { font-size: 6.3pt; color: #666; text-transform: uppercase; letter-spacing: 0.04em; }

    /* ── Contact labels (text, not emoji: emoji don't survive the PDF renderer) ── */
    .contactRow { display: flex; align-items: baseline; gap: 0.15cm; }
    .contactLabel { flex: 0 0 1.45cm; font-size: 6.3pt; color: #777; text-transform: uppercase; letter-spacing: 0.02em; }
    .contactValue { flex: 1; min-width: 0; word-break: break-word; }

    /* ── Headline under the name ── */
    .headline { font-size: 9pt; color: #333; margin-top: 0.06cm; }

    /* Keep entries whole across page breaks */
    .eduEntry, .pubEntry, .timeline li, .item, .nameBlock { page-break-inside: avoid; break-inside: avoid; }

    /* ── Footer ── */
    .cvFooter {
      margin-top: 0.3cm;
      padding-top: 0.15cm;
      border-top: 0.04cm solid #d0d4d8;
      font-size: 7pt;
      color: #aaa;
    }

    a { text-decoration: none; }
  </style>
</head>
<body>
<page size="A4">
  <div class="container">

    <!-- LEFT PANEL -->
    <div class="leftPanel">

      ${photo ? `
      <div class="profilePhoto">
        <img src="${photo}" alt="" />
      </div>` : ''}

      <div class="details">

        <!-- Contact -->
        <div class="item">
          <h2>Contact</h2>
          <div class="smallText">
            ${!blank(user?.email) ? `<p class="contactRow"><span class="contactLabel">Email</span><span class="contactValue"><a href="mailto:${esc(user.email)}">${esc(user.email)}</a></span></p>` : ''}
            ${!blank(user?.contact_number) ? `<p class="contactRow"><span class="contactLabel">Phone</span><span class="contactValue">${esc(user.contact_number)}</span></p>` : ''}
            ${!blank(user?.location) || !blank(user?.city) ? `<p class="contactRow"><span class="contactLabel">Location</span><span class="contactValue">${esc(blank(user?.location) ? user.city : user.location)}</span></p>` : ''}
            ${httpUrl(user?.website) ? `<p class="contactRow"><span class="contactLabel">Web</span><span class="contactValue"><a href="${esc(user.website)}">${esc(user.website.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, ''))}</a></span></p>` : ''}
            ${!blank(user?.orcid) ? `<p class="contactRow"><span class="contactLabel">ORCID</span><span class="contactValue"><a href="https://orcid.org/${esc(user.orcid)}">${esc(user.orcid)}</a></span></p>` : ''}
            ${httpUrl(user?.google_scholar) ? `<p class="contactRow"><span class="contactLabel">Scholar</span><span class="contactValue"><a href="${esc(user.google_scholar)}">Google Scholar profile</a></span></p>` : ''}
          </div>
        </div>

        <!-- Education -->
        ${education && education.length > 0 ? `
        <div class="item">
          <h2>Education</h2>
          ${education.map(edu => `
          <div class="eduEntry">
            <div class="eduDegree">${esc(edu.degree)}${edu.field_of_study ? ` — ${esc(edu.field_of_study)}` : ''}</div>
            <div class="eduInstitution">${esc(edu.institution)}${edu.location ? `, ${esc(edu.location)}` : ''}</div>
            <div class="eduDates">${span(edu.start_date, edu.end_date, edu.is_current)}</div>
          </div>`).join('')}
        </div>` : ''}

        <!-- Research metrics -->
        ${metrics.length ? `
        <div class="item">
          <h2>Research Metrics</h2>
          <div class="metrics">
            ${metrics.map(([label, value]) => `<div class="metric"><div class="metricValue">${esc(value)}</div><div class="metricLabel">${label}</div></div>`).join('')}
          </div>
        </div>` : ''}

        <!-- Research interests -->
        ${interests.length ? `
        <div class="item">
          <h2>Research Interests</h2>
          <div class="chips">${interests.slice(0, 12).map((x) => `<span class="chip">${esc(x)}</span>`).join('')}</div>
        </div>` : ''}

        <!-- Skills -->
        ${skills.length ? `
        <div class="item">
          <h2>Skills</h2>
          <div class="chips">${skills.slice(0, 20).map((x) => `<span class="chip">${esc(x)}</span>`).join('')}</div>
        </div>` : ''}

      </div>
    </div>

    <!-- RIGHT PANEL -->
    <div class="rightPanel">

      <!-- Name block -->
      <div class="nameBlock">
        <h1>${esc(fullName)}</h1>
        ${!blank(user?.position) ? `<h3>${esc(user.position)}</h3>` : ''}
        ${!blank(user?.headline) ? `<div class="headline">${esc(user.headline)}</div>` : ''}
      </div>

      <!-- Summary -->
      ${user?.bio ? `
      <div class="section">
        <h2>Profile</h2>
        <div class="smallText"><p style="white-space: pre-wrap; text-align: justify;">${esc(user.bio)}</p></div>
      </div>` : ''}

      <!-- Work Experience -->
      ${(() => {
        // Merge affiliations (myOrganizations) and work experience entries
        const affiliationEntries = (myOrganizations || []).map(org => ({
          _type: 'affiliation',
          position: org.membership_role || '',
          organization: org.name || '',
          department: org.membership_department_name || '',
          start_date: org.membership_start_date || '',
          end_date: null,
          is_current: org.membership_status === 'active',
          description: '',
        }));
        const workEntries = (workExperience || []).map(w => ({ _type: 'work', ...w }));
        // Sort: current first, then by start_date descending
        const all = [...affiliationEntries, ...workEntries].sort((a, b) => {
          if (a.is_current && !b.is_current) return -1;
          if (!a.is_current && b.is_current) return 1;
          return (b.start_date || '').localeCompare(a.start_date || '');
        });
        if (!all.length) return '';
        return `
      <div class="section">
        <h2>Professional Experience &amp; Affiliations</h2>
        <ul class="timeline">
          ${all.map(w => `
          <li>
            <div class="jobRow">
              <span class="jobTitle">${esc(w.position)}</span>
              <span class="jobDates">${span(w.start_date, w.end_date, w.is_current)}</span>
            </div>
            <div class="jobOrg">${esc(w.organization)}${w.department ? ` · ${esc(w.department)}` : ''}${w.location ? `, ${esc(w.location)}` : ''}</div>
            ${w.description ? `<div class="jobDesc">${esc(w.description)}</div>` : ''}
          </li>`).join('')}
        </ul>
      </div>`;
      })()}

      <!-- Key Publications -->
      ${sortedPubs.length > 0 ? `
      <div class="section">
        <h2>Key Publications</h2>
        ${sortedPubs.slice(0, 10).map(pub => {
          let authorText = pub.authors || '';
          if (pub.authors) {
            const authorList = pub.authors.split(',').map(a => a.trim());
            authorText = authorList.length > 3 ? authorList.slice(0, 3).join(', ') + ' et al.' : authorList.join(', ');
          }
          const doi = pub.doi ? String(pub.doi).replace(/^https?:\/\/(dx\.)?doi\.org\//i, '') : '';
          const meta = [esc(authorText), esc([pub.journal, pub.volume ? `${pub.volume}` : null, pub.year].filter(Boolean).join(', ')),
            doi ? `<a href="https://doi.org/${esc(doi)}">doi:${esc(doi)}</a>` : null,
            pub.citation_count > 0 ? `Cited ${esc(pub.citation_count)}×` : null].filter(Boolean).join(' · ');
          return `
        <div class="pubEntry">
          <div class="pubTitle">${esc(pub.title)}</div>
          <div class="pubMeta">${meta}</div>
        </div>`;
        }).join('')}
        ${sortedPubs.length > 10 ? `<div class="smallText"><p>+ ${sortedPubs.length - 10} further publications</p></div>` : ''}
      </div>` : ''}

      <!-- Projects -->
      ${pastProjects && pastProjects.length > 0 ? `
      <div class="section">
        <h2>Research Projects</h2>
        <ul class="timeline">
          ${pastProjects.map(project => `
          <li>
            <div class="jobRow">
              <span class="jobTitle">${esc(project.title)}</span>
              <span class="jobDates">${span(project.start_date, project.end_date, false)}</span>
            </div>
            ${project.role ? `<div class="jobOrg">Role: ${esc(project.role)}</div>` : ''}
            ${project.budget ? `<div class="jobDesc">Budget: €${parseFloat(project.budget).toLocaleString('en-US')}</div>` : ''}
            ${project.description ? `<div class="jobDesc">${esc(project.description)}</div>` : ''}
          </li>`).join('')}
        </ul>
      </div>` : ''}

      <div class="cvFooter">${esc(fullName)} · curriculum vitae · ${today} · generated with Otable (otable.net)</div>

    </div>
  </div>
</page>
</body>
</html>
      `;

      // Extract CSS and body content from the HTML string
      const styleMatch = htmlContent.match(/<style>([\s\S]*?)<\/style>/);
      const rawCss = styleMatch ? styleMatch[1] : '';
      const bodyMatch = htmlContent.match(/<body>([\s\S]*?)<\/body>/);
      const bodyContent = bodyMatch ? bodyMatch[1] : htmlContent;

      // Strip `body {}` so it doesn't resize the live page during rendering.
      const pageCSS = rawCss.replace(/\bbody\s*\{[^}]*\}/g, '');

      // Inject resume styles into document head so html2canvas / getComputedStyle
      // resolves them on the clone html2pdf places in the DOM.
      const styleEl = document.createElement('style');
      styleEl.textContent = pageCSS;
      document.head.appendChild(styleEl);

      // NO position/z-index on this element. html2pdf clones it via deepCloneBasic
      // and any inline positioning on the source is copied to the clone, causing the
      // clone to escape html2pdf's A4 container → blank canvas. Body-level styles
      // are applied here directly since the body rule was stripped above.
      const container = document.createElement('div');
      container.innerHTML = bodyContent;
      container.style.cssText = [
        'width:21cm',
        'background:#fff',
        "font-family:'Calibri','Gill Sans MT',Arial,sans-serif",
        'font-size:9pt',
        'color:#1a1a1a',
      ].join(';');

      // html2pdf creates its own opacity:0 overlay — no custom one needed.
      const opt = {
        margin: 0,
        filename: `${fullName.replace(/\s+/g, '_')}_CV.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        // Never cut an entry in half across pages; keep links (web, ORCID, DOI) clickable in the PDF
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.eduEntry', '.pubEntry', '.timeline li', '.item', '.nameBlock'] },
        enableLinks: true,
      };

      try {
        await html2pdf().set(opt).from(container).save();
      } finally {
        document.head.removeChild(styleEl);
      }
      
      setSnackbar({ 
        open: true, 
        message: 'Resume downloaded successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error generating resume:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to generate resume. Please try again.', 
        severity: 'error' 
      });
    }
  };

  const handleTabChange = (_, v) => setActiveTab(v);
  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  // Tab definitions — show_field controls public visibility; null = always visible
  // The Reflections tab: always on your own profile (so you find the page before writing anything there);
  // for visitors once there is a reflection to read. ReflectionsTab shares this request through SWR
  const { data: reflectionsInfo, mutate: refreshReflections } = useSWR(user?.id ? `/reflections/timeline/?author=${user.id}` : null, fetcher);
  // Connecting is a request the person accepts: their connections see every tab (Stats as they choose), others
  // the About tab only (the backend sends nothing more: interact/connections.py)
  const { data: followInfo } = useSWR(!isOwnProfile && user?.id ? stateUrl(user.id) : null, fetcher);
  // staff see every profile in full
  const isFollower = isOwnProfile || followInfo?.state === 'connected' || Boolean(currentUser?.is_staff);
  const FOLLOWERS_ONLY = ['experience', 'publications', 'projects'];
  const ALL_PROFILE_TABS = [
    { key: 'about',        label: 'About',                    showField: null },
    { key: 'experience',   label: 'Experience',               showField: 'tab_show_experience' },
    { key: 'publications', label: 'Publications',   showField: 'tab_show_publications' },
    { key: 'projects',     label: 'Projects',                 showField: 'tab_show_projects' },
    // others: once there is one they may read (connections: all; everyone else: those shared with everyone on Otable)
    ...(isOwnProfile || (reflectionsInfo?.entries || []).length > 0 ? [{ key: 'reflections', label: 'Reflections', showField: null }] : []),
    { key: 'stats',        label: 'Stats',                    showField: null },
  ];
  // Visitors only see tabs the profile owner has made public
  const shownTabs = isOwnProfile
    ? ALL_PROFILE_TABS
    // Stats: only when the owner shows at least one card to Otable users
    : ALL_PROFILE_TABS.filter(t => (t.key === 'stats' ? (user?.public_stats || []).filter((k) => k !== 'writing_summary').length > 0
      : (!t.showField || user?.[t.showField] !== false) && (isFollower || !FOLLOWERS_ONLY.includes(t.key))));
  const activeTabKey = shownTabs[activeTab]?.key || 'about';
  // the Connect state changed while on the page (accepted, removed…): the profile again, quietly. Not when the
  // state first arrives with the page: that reloaded the profile on every visit, and it flashed
  const followStateNow = followInfo?.state;
  const lastFollowState = useRef({ id: null, state: undefined });
  useEffect(() => {
    const seen = lastFollowState.current;
    if (seen.id !== profileId) {
      lastFollowState.current = { id: profileId, state: followStateNow };
      return;
    }
    if (seen.state === undefined) { seen.state = followStateNow; return; }
    if (!isOwnProfile && followStateNow && followStateNow !== seen.state && profileId) {
      seen.state = followStateNow;
      fetchUserProfile(profileId, { quiet: true });
      refreshReflections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followStateNow, profileId]);

  // the guide shows the About tab (organisations, skills, interests) and scrolls each step's element into view
  const guide = useGuideTour({ storageKey: 'profile.guide.dismissed.v1', steps: PROFILE_GUIDE_STEPS,
    targetRefs: PROFILE_GUIDE_TARGETS, autoOpen: false, recalcDeps: [activeTab, loading] });
  const showGuide = () => { setActiveTab(0); guide.show(); };
  useEffect(() => {
    if (!guide.open) return;
    const key = [].concat(PROFILE_GUIDE_STEPS[guide.step]?.target || []).join('|');
    PROFILE_GUIDE_TARGETS[key]?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [guide.open, guide.step]);


  /* ---------- UI ---------- */

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}><Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} /></Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Stack>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Profile not found or could not be loaded.</Alert>
      </Container>
    );
  }

  const displayName = computeDisplayName(user);

  return (
    <PageContainer>
      {/* the actions are for connections; a message is possible for anyone */}
      <PageHeader title="Profile" actions={(
        <ActionButtons
          only={isFollower ? undefined : ['message']}
          user={user}
          isOwnProfile={isOwnProfile}
          setSuggestDialogOpen={setSuggestDialogOpen}
          setInviteToConsortiumDialogOpen={setInviteToConsortiumDialogOpen}
          setMeetingDialogOpen={setMeetingDialogOpen}
        />
      )}>
        {isOwnProfile && (
          <Tooltip title="How to make your profile work for you" placement="bottom">
            <IconButton onClick={showGuide} size="small" aria-label="Show Profile guide">
              <HelpOutlineIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </PageHeader>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={4}>
          <ProfileCard data-guide="profile-card">
            <CoverImage />
            <CardContent sx={{ textAlign: "center", pt: 0.5, pb: 1, px: 1.5, position: 'relative' }}>
              {editingProfile ? (
                <Stack spacing={2} sx={{ mt: -4 }}>
                  {/* Avatar Upload in Edit Mode */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      src={user.avatar} 
                      alt={displayName}
                      sx={{ width: 100, height: 100, fontSize: '5rem',
                        backgroundColor: theme.palette.primary.avatar,
                        color: theme.palette.primary.contrastText,
                       }}
                    />
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      style={{ display: 'none' }}
                      id="avatar-upload-edit-card"
                      onChange={handleAvatarUpload}
                    />
                    <label htmlFor="avatar-upload-edit-card">
                      <Button
                        component="span"
                        variant="outlined"
                        size="small"
                        startIcon={uploadingAvatar ? <CircularProgress size={14} /> : <CameraIcon />}
                        disabled={uploadingAvatar}
                      >
                        {uploadingAvatar ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                    {user.avatar && (
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={handleRemoveAvatar}
                        disabled={uploadingAvatar}
                      >
                        Remove Photo
                      </Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Max 5MB. JPG, PNG, GIF
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ py: 1 }} />
                  
                  <TextField 
                    size="small" 
                    label="First Name" 
                    value={profileData.first_name}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                    onBlur={(e) => handleFieldBlur("first_name", e.target.value)}
                    fullWidth 
                  />
                  <TextField 
                    size="small" 
                    label="Last Name" 
                    value={profileData.last_name}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                    onBlur={(e) => handleFieldBlur("last_name", e.target.value)}
                    fullWidth 
                  />
                  <TextField 
                    size="small" 
                    label="Position" 
                    value={profileData.position}
                    onChange={(e) => handleInputChange("position", e.target.value)}
                    onBlur={(e) => handleFieldBlur("position", e.target.value)}
                    fullWidth 
                  />
                  {updating && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">Saving...</Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <>
                  {/* Avatar in the network ring, with camera button */}
                  <RingAvatar src={user.avatar} name={displayName} size={96} sx={{ mt: '-52px' }}>
                    {isOwnProfile && (
                      <>
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          style={{ display: 'none' }}
                          id="avatar-upload-input"
                          onChange={handleAvatarUpload}
                        />
                        <label htmlFor="avatar-upload-input">
                          <Tooltip title="Change photo" arrow>
                            <IconButton
                              component="span"
                              size="small"
                              disabled={uploadingAvatar}
                              aria-label="Change photo"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                right: 2,
                                width: 32,
                                height: 32,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' },
                                boxShadow: 1,
                              }}
                            >
                              {uploadingAvatar ? <CircularProgress size={16} /> : <CameraIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Tooltip>
                        </label>
                      </>
                    )}
                  </RingAvatar>

                  <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                        {displayName}
                      </Typography>
                      {myOrganizations.some(org => org.membership_is_verified) && (
                        <VerifiedBadge size="medium" tooltipText="Verified Organization Member" />
                      )}
                    </Box>

                    {user.headline && user.headline !== 'False' && user.headline !== 'false' && (
                      <Typography sx={{ ...HEADLINE_SX, mx: 2, mt: 0.75, 
                        fontSize: '1.3rem', lineHeight: 1.25, fontWeight: 300 }}>
                        {user.headline}
                      </Typography>
                    )}

                    {user.location && user.location !== 'False' && user.location !== 'false' && (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, color: 'text.secondary' }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography sx={{ fontSize: '1.05rem' }}>{user.location}</Typography>
                      </Box>
                    )}

                    {/* Row 4: Web Links */}
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                      {user.website && user.website !== 'False' && user.website !== 'false' && (
                        <IconButton
                          component="a"
                          href={user.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="medium"
                          title="Homepage"
                          sx={{ p: 0.5 }}
                        >
                          <LanguageIcon fontSize="medium" />
                        </IconButton>
                      )}
                      {user.orcid && user.orcid !== 'False' && user.orcid !== 'false' && (
                        <IconButton
                          component="a"
                          href={`https://orcid.org/${user.orcid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="medium"
                          title="ORCID"
                          sx={{ p: 0.5 }}
                        >
                          <Box
                            component="img"
                            src="https://orcid.org/assets/vectors/orcid.logo.icon.svg"
                            alt="ORCID"
                            sx={{ width: 20, height: 20 }}
                          />
                        </IconButton>
                      )}
                      {user.google_scholar && user.google_scholar !== 'False' && user.google_scholar !== 'false' && (
                        <IconButton
                          component="a"
                          href={user.google_scholar}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="medium"
                          title="Google Scholar"
                          sx={{ p: 0.5 }}
                        >
                          <SchoolIcon fontSize="medium" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ py: 1 }} />

                  {isOwnProfile && (
                    <Button 
                      variant="outlined" 
                      size="medium"
                      startIcon={<EditIcon />}
                      onClick={handleEditProfile} 
                      sx={{ mt: 1.5, fontSize: '1rem' }}
                      disabled={updating}
                    >
                      Edit Profile
                    </Button>
                  )}
                  {isOwnProfile && (
                    <Stack data-guide="profile-import" direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setCvImportOpen(true)}
                      >
                        Import from CV
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<PublicIcon />}
                        onClick={() => setPublicImportOpen(true)}
                      >
                        Fill from ORCID / web
                      </Button>
                    </Stack>
                  )}
                 {!isOwnProfile && (
                        // Connect sends a request; Requested until they accept; then Connected
                        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                          <FollowButton
                            contentType="user"
                            objectId={profileId}
                            size="medium"
                            variant="outlined"
                            sx={{ fontSize: '1rem' }}
                          />
                        </Box>
                      )}
                 

                </>
                
              )}
            </CardContent>
          </ProfileCard>

          {isOwnProfile && (
            <Box data-guide="profile-completeness">
              <ProfileCompletenessCard
                onImportCV={() => setCvImportOpen(true)}
                refreshKey={completenessKey}
              />
            </Box>
          )}

          {/* Consortia Card */}
          {myOrganizations.length > 0 && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Organizations</Typography>
                  {isOwnProfile && (
                    <IconButton size="small" onClick={openAddAffiliationDialog}>
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
                <Stack spacing={1.5}>
                  {myOrganizations.map(org => (
                    <Box 
                      key={org.id} 
                      sx={{ 
                        p: 1.5, 
                        // border: 1, 
                        boxShadow: 2,
                        borderRadius: 1,
                        background: bgtheme.backgroundColor,
                        cursor: 'pointer',
                    
                      }}
                      onClick={() => navigate(`/organization/${org.id}`)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" fontSize="small" />
                        <Typography variant="body2" fontWeight={700} lineHeight={1.4} color="text.secondary">
                          {org.name}
                        </Typography>
                        {org.membership_is_verified && (
                          <VerifiedBadge size="small" tooltipText="Verified Member" />
                        )}
                      </Box>
                      {org.membership_role && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5 }}>
                          { org.membership_role.charAt(0).toUpperCase() + org.membership_role.slice(1) }
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Paper>
          )}

          {/* Writing activity: the summary (details on the Stats tab); visitors see it when the owner shares it */}
          {user?.id && (
            <WritingSummaryCard userId={user.id} isOwn={isOwnProfile}
              onOpenStats={() => { const i = shownTabs.findIndex((t) => t.key === 'stats'); if (i >= 0) setActiveTab(i); }} />
          )}

          {/* What you and this person do together (someone else's profile only) */}
          {!isOwnProfile && user?.id && <TogetherCard userId={user.id} firstName={user.first_name} />}

          {/* Quick Stats cards */}
          {/* <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <StatsCard onClick={() => setActiveTab(1)}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="h6" color="primary">{user.publications_count || 0}</Typography>
                  <Typography variant="caption">Publications</Typography>
                </CardContent>
              </StatsCard>
            </Grid>
            <Grid item xs={6}>
              <StatsCard onClick={() => setActiveTab(1)}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="h6" color="secondary">{user.collaborations_count || 0}</Typography>
                  <Typography variant="caption">Collaborations</Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          </Grid> */}
        </Grid>

        {/* Right column: Tabs */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              position: 'sticky',
              top: 64,
              zIndex: 10,
              backgroundColor: 'background.paper',
              borderRadius: editingProfile ? '8px 8px 0 0' : '8px 8px 0 0',
            }}>
              <Tabs
                data-guide="profile-tabs"
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                // smaller labels so all the tabs fit; the first one starts at the container's left edge
                sx={{ flex: 1, minWidth: 0, '& .MuiTabs-scrollButtons.Mui-disabled': { width: 0 },
                  '& .MuiTab-root': {  
                    fontSize: '1.25rem', 
                    minWidth: 0, 
                    px: 1.75,
                    },
                  '& .MuiTab-root:first-of-type': { pl: 2 } }}
              >
                {shownTabs.map(t => (
                  <Tab key={t.key} label={t.label} />
                ))}
              </Tabs>
              {isOwnProfile ? (
                (editingProfile || editingResume) ? (
                  <Box sx={{ display: 'flex', gap: 0.5, mx: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="AI ideas">
                      <IconButton
                        onClick={() => setIdeasOpen(true)}
                        color="primary"
                        aria-label="AI ideas"
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download Resume (PDF)">
                      <IconButton
                        onClick={handleDownloadResume}
                        color="primary"
                        aria-label="Download resume (PDF)"
                        sx={{ mr: 1 }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              ):
              (
                <ReportButton
                  targetType="user"
                  targetId={user?.id}
                  targetLabel={user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
                  size="small"
                  sx={{ mr: 2 }}
                />
              )
              }


            </Box>

            {/* not connected: what connecting opens */}
            {!isFollower && user && (
              <Box sx={{ mx: 3, mt: 2.5, px: 2, py: 1.25, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LockOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {followInfo?.state === 'requested'
                    ? `Your request is waiting for ${displayName.split(' ')[0]} to accept. Then you will see their experience, publications, projects and the reflections they share with connections.`
                    : `Connect with ${displayName.split(' ')[0]} to see their experience, publications, projects and the reflections they share with connections. They accept your request first.`}
                </Typography>
              </Box>
            )}

            {/* About */}
            {activeTabKey === 'about' && (
              <Box sx={{ p: 3, minHeight: '60vh' }}>
                {editingProfile ? (
                  // <Stack spacing={4}>
                  //   {/* Basic Information */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  //     <Stack spacing={2}>
                  //       <TextField label="Email" type="email" value={profileData.email}
                  //         onChange={(e) => handleInputChange("email", e.target.value)} fullWidth />
                        
                  //       <TextField label="Headline" value={profileData.headline}
                  //         onChange={(e) => handleInputChange("headline", e.target.value)}
                  //         fullWidth placeholder="A brief professional headline (e.g., AI Researcher | Data Scientist)" 
                  //         helperText="Max 160 characters" />
                        
                  //       <TextField label="Bio" value={profileData.bio}
                  //         onChange={(e) => handleInputChange("bio", e.target.value)}
                  //         fullWidth multiline rows={4}
                  //         placeholder="Tell us about your research interests and background..." />
                  //     </Stack>
                  //   </Box>

                  //   <Divider />

                  //   {/* Location */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Location</Typography>
                  //     <Stack spacing={2}>
                  //       <TextField label="City" value={profileData.city}
                  //         onChange={(e) => handleInputChange("city", e.target.value)} fullWidth />
                        
                  //       <Autocomplete
                  //         options={countries}
                  //         getOptionLabel={(option) => option?.name || ''}
                  //         value={profileData.country}
                  //         onChange={(event, newValue) => handleInputChange("country", newValue)}
                  //         renderInput={(params) => <TextField {...params} label="Country" />}
                  //         isOptionEqualToValue={(option, value) => option?.code === value?.code}
                  //         fullWidth
                  //       />
                        
                  //       <TextField label="Timezone" value={profileData.timezone}
                  //         onChange={(e) => handleInputChange("timezone", e.target.value)}
                  //         fullWidth placeholder="e.g., America/New_York, Europe/London" />
                  //     </Stack>
                  //   </Box>

                  //   <Divider />

                  //   {/* Contact Information */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  //     <Stack spacing={2}>
                  //       <TextField label="Contact Number" value={profileData.contact_number}
                  //         onChange={(e) => handleInputChange("contact_number", e.target.value)} fullWidth />
                        
                  //       <TextField label="Address" value={profileData.address}
                  //         onChange={(e) => handleInputChange("address", e.target.value)} fullWidth />
                  //     </Stack>
                  //   </Box>

                  //   <Divider />

                  //   {/* Professional Links */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Professional Links</Typography>
                  //     <Stack spacing={2}>
                  //       <TextField label="Website" type="url" value={profileData.website}
                  //         onChange={(e) => handleInputChange("website", e.target.value)} fullWidth 
                  //         placeholder="https://yourwebsite.com" />
                        
                  //       <TextField label="ORCID" value={profileData.orcid}
                  //         onChange={(e) => handleInputChange("orcid", e.target.value)}
                  //         fullWidth placeholder="0000-0000-0000-0000" 
                  //         helperText="Your ORCID identifier (https://orcid.org/)" />
                        
                  //       <TextField label="Google Scholar Profile" type="url" value={profileData.google_scholar}
                  //         onChange={(e) => handleInputChange("google_scholar", e.target.value)}
                  //         fullWidth placeholder="https://scholar.google.com/citations?user=..." />
                  //     </Stack>
                  //   </Box>
                    
                  //   <Divider />

                  //   {/* Skills Management */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Skills & Expertise</Typography>
                  //     <Stack spacing={2}>
                  //       <Box sx={{ display: 'flex', gap: 1 }}>
                  //         <TextField
                  //           size="small"
                  //           placeholder="Type a skill and press Add (e.g., Python, Machine Learning)"
                  //           value={newSkillInput}
                  //           onChange={(e) => setNewSkillInput(e.target.value)}
                  //           onKeyPress={(e) => {
                  //             if (e.key === 'Enter' && newSkillInput.trim()) {
                  //               e.preventDefault();
                  //               handleAddSkill();
                  //             }
                  //           }}
                  //           fullWidth
                  //         />
                  //         <Button
                  //           variant="contained"
                  //           size="small"
                  //           startIcon={<AddIcon />}
                  //           onClick={handleAddSkill}
                  //           disabled={!newSkillInput.trim() || updating}
                  //         >
                  //           {updating ? 'Adding...' : 'Add'}
                  //         </Button>
                  //       </Box>
                  //       {userSkills && userSkills.length > 0 && (
                  //         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  //           {userSkills.map((userSkill) => (
                  //             <Chip 
                  //               key={userSkill.id}
                  //               label={`${userSkill.skill.name} `}
                  //               onDelete={() => handleDeleteSkill(userSkill.id)}
                  //               color="primary"
                  //             />
                  //           ))}
                  //         </Box>
                  //       )}
                  //     </Stack>
                  //   </Box>

                  //   <Divider />

                  //   {/* Research Interests Management */}
                  //   <Box>
                  //     <Typography variant="h6" gutterBottom>Research Interests</Typography>
                  //     <Stack spacing={2}>
                  //       <Box sx={{ display: 'flex', gap: 1 }}>
                  //         <TextField
                  //           size="small"
                  //           placeholder="Type a research interest and press Add (e.g., Artificial Intelligence)"
                  //           value={newInterestInput}
                  //           onChange={(e) => setNewInterestInput(e.target.value)}
                  //           onKeyPress={(e) => {
                  //             if (e.key === 'Enter' && newInterestInput.trim()) {
                  //               e.preventDefault();
                  //               handleAddInterest();
                  //             }
                  //           }}
                  //           fullWidth
                  //         />
                  //         <Button
                  //           variant="contained"
                  //           size="small"
                  //           startIcon={<AddIcon />}
                  //           onClick={handleAddInterest}
                  //           disabled={!newInterestInput.trim() || updating}
                  //         >
                  //           {updating ? 'Adding...' : 'Add'}
                  //         </Button>
                  //       </Box>
                  //       {researchInterests && researchInterests.length > 0 && (
                  //         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  //           {researchInterests.map((interest) => (
                  //             <Chip 
                  //               key={interest.id}
                  //               label={interest.area.name}
                  //               onDelete={() => handleDeleteInterest(interest.id)}
                  //               color="secondary"
                  //             />
                  //           ))}
                  //         </Box>
                  //       )}
                  //     </Stack>
                  //   </Box>
                    
                  //   <Divider />
                    
                  //   <Box sx={{ display: "flex", gap: 2 }}>
                  //     <Button variant="contained" onClick={handleSaveProfile} disabled={updating}
                  //       startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />}>Save Changes</Button>
                  //     <Button variant="outlined" onClick={handleCancelEdit} disabled={updating}
                  //       startIcon={<CancelIcon />}>Cancel</Button>
                  //   </Box>
                  // </Stack>



<Stack spacing={3}>

          {/* ================= BASIC + LOCATION ================= */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Basic Information
              </Typography>

              <Grid container spacing={2} mt={0.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={(e) => handleFieldBlur("email", e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Headline"
                    value={profileData.headline}
                    onChange={(e) => handleInputChange("headline", e.target.value)}
                    onBlur={(e) => handleFieldBlur("headline", e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Bio"
                    value={profileData.bio}
                    onChange={(e) => handleInputChange("bio", e.target.value)}
                    onBlur={(e) => handleFieldBlur("bio", e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="City"
                    value={profileData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    onBlur={(e) => handleFieldBlur("city", e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={countries}
                    value={profileData.country}
                    onChange={(e, v) => {
                      handleInputChange("country", v);
                      handleFieldBlur("country", v);
                    }}
                    getOptionLabel={(o) => o?.name || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Country" />
                    )}
                  />
                </Grid>

                {/* <Grid item xs={12} md={4}>
                  <TextField
                    label="Timezone"
                    value={profileData.timezone}
                    onChange={(e) => handleInputChange("timezone", e.target.value)}
                    fullWidth
                  />
                </Grid> */}
              </Grid>
            </CardContent>
          </Card>

          {/* ================= CONTACT + LINKS ================= */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}> 
                Contact & Links
              </Typography>

              <Grid container spacing={2}>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="Contact Number"
                    value={profileData.contact_number}
                    onChange={(e) => handleInputChange("contact_number", e.target.value)}
                    fullWidth
                  />
                </Grid> */}

                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="Address"
                    value={profileData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    fullWidth
                  />
                </Grid> */}

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Website"
                    value={profileData.website}
                    onChange={(e) => handleInputChange("website", e.target.value)}
                    onBlur={(e) => handleFieldBlur("website", e.target.value)}
                    fullWidth
                    placeholder="example.com"
                    helperText="https:// will be added automatically if omitted"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="ORCID"
                    value={profileData.orcid}
                    onChange={(e) => handleInputChange("orcid", e.target.value)}
                    onBlur={(e) => handleFieldBlur("orcid", e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Google Scholar"
                    value={profileData.google_scholar}
                    onChange={(e) =>
                      handleInputChange("google_scholar", e.target.value)
                    }
                    onBlur={(e) => handleFieldBlur("google_scholar", e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ================= SKILLS ================= */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Skills & Expertise
              </Typography>

              <Stack spacing={1.5}>
                <Box display="flex" gap={1}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    freeSolo
                    value={newSkillInput}
                    onChange={(event, newValue) => setNewSkillInput(newValue)}
                    inputValue={typeof newSkillInput === 'string' ? newSkillInput : newSkillInput?.name || ''}
                    onInputChange={(event, newInputValue) => {
                      // For free text input
                      if (typeof newSkillInput === 'string' || !newSkillInput) {
                        setNewSkillInput(newInputValue);
                      }
                    }}
                    options={availableSkills}
                    getOptionLabel={(option) => 
                      typeof option === 'string' ? option : option.name || ''
                    }
                    loading={loadingSkills}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Add skill from call skills or type new..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingSkills ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddSkill}
                    disabled={updating || (!newSkillInput)}
                  >
                    Add
                  </Button>
                </Box>

                {userSkills?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Star up to {FEATURED_SKILLS} skills to show on your card in the Collaboration Hub
                    ({userSkills.filter((s) => s.featured).length}/{FEATURED_SKILLS}).
                  </Typography>
                )}
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {userSkills?.map((s) => {
                    const full = !s.featured && userSkills.filter((x) => x.featured).length >= FEATURED_SKILLS;
                    return (
                      <Tooltip key={s.id} title={s.featured ? 'Shown on your hub card: click to unpick'
                        : full ? `${FEATURED_SKILLS} already picked` : 'Show on your hub card'}>
                        <Chip
                          label={s.skill.name}
                          icon={s.featured ? <StarIcon /> : <StarBorderIcon />}
                          onClick={() => handleToggleFeaturedSkill(s)}
                          onDelete={() => handleDeleteSkill(s.id)}
                          sx={{
                            bgcolor: s.featured ? theme.palette.primary.light : theme.palette.primary.xulight,
                            color: s.featured ? '#fff' : undefined,
                            fontWeight: s.featured ? 500 : 300,
                            opacity: full ? 0.7 : 1,
                            '& .MuiChip-icon': { color: s.featured ? '#ffd54f' : 'text.secondary', fontSize: 18 },
                            '& .MuiChip-deleteIcon': { color: s.featured ? 'rgba(255,255,255,0.8)' : undefined },
                            '&:hover': { bgcolor: s.featured ? theme.palette.primary.main : undefined },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* ================= INTERESTS ================= */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Research Interests
              </Typography>

              <Stack spacing={1.5}>
                <Box display="flex" gap={1}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    freeSolo
                    value={newInterestInput}
                    onChange={(event, newValue) => setNewInterestInput(newValue)}
                    inputValue={typeof newInterestInput === 'string' ? newInterestInput : newInterestInput?.name || ''}
                    onInputChange={(event, newInputValue) => {
                      // For free text input
                      if (typeof newInterestInput === 'string' || !newInterestInput) {
                        setNewInterestInput(newInputValue);
                      }
                    }}
                    options={availableInterests}
                    getOptionLabel={(option) => 
                      typeof option === 'string' ? option : option.name || ''
                    }
                    loading={loadingInterests}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Add interest from areas or type new..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingInterests ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddInterest}
                    disabled={updating || (!newInterestInput)}
                  >
                    Add
                  </Button>
                </Box>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {researchInterests?.map((i) => (
                    <Chip
                      key={i.id}
                      label={i.category.name}
                      sx={{
                        bgcolor: theme.palette.primary.xulight,
                        // color: theme.palette.primary.contrastText,
                        fontWeight: 300 }}
                      onDelete={() => handleDeleteInterest(i.id)}
                    
                    />
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* ================= AUTO-SAVE INDICATOR ================= */}
          {updating && (
            <Box display="flex" justifyContent="center" alignItems="center" gap={1} py={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Saving changes...</Typography>
            </Box>
          )}

</Stack>

                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="h3">About</Typography>
                      {isOwnProfile && (
                        <IconButton onClick={handleEditProfile} disabled={updating} aria-label="Edit profile"><EditIcon /></IconButton>
                      )}
                    </Box>

                    <Typography variant="body1" paragraph>
                      {user.bio || "No bio available. Add a bio to tell others about your research interests and professional background."}
                    </Typography>

                    <Stack spacing={2.5}>
                      {/* {user.timezone && user.timezone !== 'False' && user.timezone !== 'false' && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationIcon />
                          <Typography>Timezone: {user.timezone}</Typography>
                        </Box>
                      )} */}
                      
                      {/* {user.contact_number && user.contact_number !== 'False' && user.contact_number !== 'false' && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PhoneIcon />
                          <Typography>Contact: {user.contact_number}</Typography>
                        </Box>
                      )} */}
                      
                      {/* {user.address && user.address !== 'False' && user.address !== 'false' && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LocationIcon />
                          <Typography>Address: {user.address}</Typography>
                        </Box>
                      )} */}
                   
                      {isOwnProfile && !user.contact_number && !user.address && (!userSkills || userSkills.length === 0) && (!researchInterests || researchInterests.length === 0) && !user.orcid && !user.google_scholar && !user.timezone && (
                        <Alert severity="info">
                          <Typography variant="body1">
                            Complete your profile by adding contact information, research identifiers, skills, and research interests.
                          </Typography>
                        </Alert>
                      )}
                    </Stack>

                    <Divider sx={{ py: 1 }} />

                    {/* Organizations Section */}
                    <Box data-guide="profile-organizations" pt={2} >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="h3">My Organizations</Typography>
                        {isOwnProfile && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                      
                            <Button 
                              size="small" 
                              variant="outlined" 
                              startIcon={<AddIcon />}
                              onClick={openAddAffiliationDialog} 
                              disabled={loadingMyOrgs || updating}
                            >
                              Add Affiliation
                            </Button>
                          
                          {/* <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={refreshMyOrganizations} 
                            disabled={loadingMyOrgs || updating}
                          >
                            Refresh
                          </Button> */}
                          
                        </Box> )}
                      </Box>
                      {loadingMyOrgs ? (
                        <CircularProgress size={24} />
                      ) : myOrganizations.length === 0 ? (
                        <Box>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            You are not a member of any organizations yet.
                          </Typography>
                          {isOwnProfile && (
                            <Button 
                              variant="contained" 
                              startIcon={<AddIcon />}
                              onClick={openAddAffiliationDialog}
                            >
                              Add Your First Affiliation
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Stack spacing={1.5}>
                          {myOrganizations.map((org) => (
                            <Paper 
                              key={org.id}
                              sx={{  
                                p: 1.5, 
                                px: 2,
                                position: 'relative', 
                                // border: .5, 
                                // borderColor: theme.palette.primary.xulight, 
                                

                                background: bgtheme.backgroundColor,

                                borderRadius: 1,
                                border: 1,
                                borderColor: theme.palette.grey[50], 
                                boxShadow: theme.shadows[1],
                                // cursor: 'pointer',
                                // '&:hover': { bgcolor: theme.palette.grey[50] },
                                // transition: 'background-color 0.2s',
                              }}
                                      >
                              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Typography 
                                      variant="body1" 
                                      fontWeight={600}
                                      lineHeight={1.4}
                                      onClick={() => navigate(`/organization/${org.id}`)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        // '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {org.name}
                                    </Typography>
                                    {org.membership_is_verified && (
                                      <VerifiedBadge size="medium" tooltipText="Verified Member" />
                                    )}
                                  </Box>
                                  {/* <Typography variant="caption" color="text.secondary">
                                    Members: {org.memberships_count ?? org.members_count ?? '—'} • Departments: {org.departments_count ?? '—'}
                                  </Typography> */}

                                  {org.membership_department_name && (
                                    <Typography 
                                      variant="body2" 
                                      color="primary.main"
                                      onClick={() => navigate(`/organization/${org.id}/department/${org.membership_department_id}`)}
                                      sx={{ 
                                        display: 'block', 
                                        mt: 0.5,
                                        cursor: 'pointer',
                                        // '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      Department: {org.membership_department_name}
                                    </Typography>
                                  )}

                                  {org.membership_role && (
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                      <Chip label={  org.membership_role.charAt(0).toUpperCase() + org.membership_role.slice(1)} size="small" sx={{ mr: 0.5 }} />
                                      {org.membership_is_primary && <Chip label="Primary" size="small" color="primary" />}
                                      {org.membership_is_verified && org.membership_can_edit_org && (
                                        <Chip label="Can Edit" size="small" color="success" variant="outlined" />
                                      )}
                                      {!org.membership_is_verified && (
                                        <Chip label="Unverified" size="small" color="warning" variant="outlined" />
                                      )}
                                    </Box>
                                  )}
                          
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {isOwnProfile && org.membership_id && (
                                    <>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => openEditAffiliationDialog({
                                          membership_id: org.membership_id,
                                          organization_id: org.id,
                                          organization_name: org.name,
                                          role: org.membership_role,
                                          department_id: org.membership_department_id,
                                          department_name: org.membership_department_name,
                                          start_date: org.membership_start_date,
                                          is_primary: org.membership_is_primary,
                                          status: org.membership_status
                                        })}
                                        disabled={updating}
                                        title="Edit affiliation"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteAffiliation(org.membership_id)}
                                        disabled={updating}
                                        title="Remove affiliation"
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  )}
                                  
                                  {!org.membership_is_verified && isOwnProfile && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={() => openVerifyDialog({
                                        membership_id: org.membership_id,
                                        organization_name: org.name,
                                        user_name: user?.name || `${user?.first_name} ${user?.last_name}`,
                                      })}
                                      disabled={updating}
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      Verify organization
                                    </Button>
                                  )}
                                  
                                  {isOwnProfile && org.membership_is_verified && (
                                    <>
                                      {editingOrgDescription === org.id ? (
                                        <>
                                          <Button size="small" variant="contained" onClick={() => saveOrgDescription(org.id)} disabled={updating}>Save</Button>
                                          <Button size="small" variant="outlined" onClick={cancelEditOrgDescription} disabled={updating}>Cancel</Button>
                                        </>
                                      ) : (
                                        <Button size="small" variant="text" onClick={() => navigate(`/organization/${org.id}`)} disabled={updating}>Edit Organization</Button>
                                      )}
                                    </>
                                  )}
                                  {isOwnProfile && !org.membership_is_verified && (
                                    <Chip label="Verify to edit" size="small" color="warning" variant="outlined" />
                                  )}
                                </Box>
                              </Box>


                              
{/* 
                              {editingOrgDescription === org.id ? (
                                <TextField
                                  multiline
                                  minRows={3}
                                  fullWidth
                                  value={orgDescriptionDraft}
                                  onChange={(e) => setOrgDescriptionDraft(e.target.value)}
                                  placeholder="Update organization description"
                                />
                              ) : (
                                <Typography variant="body2" sx={{ whiteSpace:'pre-wrap', mb:2 }}>
                                  {org.description || 'No description provided.'}
                                </Typography>
                              )} */}
                            </Paper>



                          ))}
                        </Stack>
                        
                      )}

                      {/* Labs this person belongs to */}
                      {userLabs.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {isOwnProfile ? 'My Labs' : 'Labs'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {userLabs.map((lab) => {
                              const c = /^#[0-9a-f]{6}$/i.test(lab.background_color || '') ? lab.background_color : '#5b5fc7';
                              const me = (lab.member_people || []).find((m) => String(m.id) === String(user.id));
                              const role = String(lab.pi) === String(user.id) ? 'PI' : me?.lab_title;
                              return (
                                <Box
                                  key={lab.id}
                                  role="link"
                                  tabIndex={0}
                                  onClick={() => navigate(`/lab/${lab.id}/`)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/lab/${lab.id}/`); }}
                                  sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, pl: 0.75, pr: 1.5, py: 0.75,
                                    borderRadius: 2, cursor: 'pointer', border: '1px solid', borderColor: `${c}40`,
                                    bgcolor: `${c}0d`, transition: 'background-color 0.2s', maxWidth: '100%',
                                    '&:hover': { bgcolor: `${c}1f` },
                                  }}
                                >
                                  <Avatar
                                    variant="rounded"
                                    src={lab.cover_url || undefined}
                                    sx={{ width: 30, height: 30, bgcolor: c, fontSize: '0.85rem', fontWeight: 700 }}
                                  >
                                    {(lab.name || 'L')[0]}
                                  </Avatar>
                                  <Box minWidth={0}>
                                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: c, lineHeight: 1.25 }}>{lab.name}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.2 }}>
                                      {[role, lab.organization_detail?.short_name || lab.organization_detail?.name].filter(Boolean).join(' · ')}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                    </Box>

                                        <Divider sx={{ mt: 3 }} />
                    
                      {/* Skills Section */}
                      {userSkills && userSkills.length > 0 && (
                        <Box data-guide="profile-skills" sx={{ bgcolor: 'primary.50', borderRadius: 2,  mt:2}}>
                          <Typography variant="h3" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* <ScienceIcon color="primary" /> */}
                            Skills & Expertise
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {userSkills.map((userSkill) => (
                              // <Tooltip 
                              //   key={userSkill.id}
                              //   title={`Proficiency: ${userSkill.proficiency} | Experience: ${userSkill.years_exp} years${userSkill.endorsements > 0 ? ` | ${userSkill.endorsements} endorsements` : ''}`}
                              //   arrow
                              // >
                                <Chip 
                                  label={`${userSkill.skill.name}`}
                                  size="medium"
                                  
                                  sx={{bgcolor: theme.palette.primary.xulight,
                                  // color: theme.palette.primary.contrastText,
                                  fontWeight: 350 }}
                                />
                              // </Tooltip>
                            ))}
                          </Box>

                          <Divider sx={{ mt: 3 }} />

                        </Box>
                      )}
                      


                      {/* Research Interests Section */}
                      {researchInterests && researchInterests.length > 0 && (
                        <Box data-guide="profile-interests" sx={{  bgcolor: 'secondary.50', borderRadius: 2, mt: 2 }}>
                          <Typography variant="h3" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* <SchoolIcon color="secondary" /> */}
                            Research Interests
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {researchInterests.map((interest) => (
                              // <Tooltip 
                              //   key={interest.id}
                              //   title={`Interest Level: ${interest.weight}/10${interest.category.code ? ` | Code: ${interest.category.code}` : ''}`}
                              //   arrow
                              // >
                              //    </Tooltip>
                                <Chip 
                                  label={interest.category.name}
                                  size="medium"
                                  
                                  sx={{
                                    bgcolor: theme.palette.primary.xulight,
                                    // color: theme.palette.primary.contrastText,
                                    fontWeight: 350 }}

                                />
                             
                            ))}
                          </Box>
                        </Box>
                      )}



                  </>
                )}
              </Box>
            )}


            {/* Resume / Experience */}
            {activeTabKey === 'experience' && (

  <Stack spacing={0} sx={{ px: 3, minHeight: '500px' }}>

{/* Short Resume */}
  <Box sx={{  pt:3, mb: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="h3">Summary</Typography>
      {isOwnProfile && (
        <IconButton onClick={() => setEditingResume(true)} disabled={updating}>
          <EditIcon />
        </IconButton>
      )}
    </Box>


    {editingResume ? (
      <Stack spacing={3}>
        <Typography variant="body1" color="text.secondary">
          Write a short professional summary highlighting your key achievements and expertise.
        </Typography>
        <TextField
          label="Professional Resume"
          multiline
          rows={10}
          value={resume}
          onChange={(e) => setResume(e.target.value)}
          onBlur={async (e) => {
            try {
              setUpdating(true);
              await axiosService.patch(`/user-profiles/me/`, { resume: e.target.value });
              setSnackbar({ open: true, message: 'Resume updated successfully', severity: 'success' });
            } catch (err) {
              console.error('Error saving resume:', err);
              setSnackbar({ open: true, message: 'Failed to save resume', severity: 'error' });
            } finally {
              setUpdating(false);
            }
          }}
          placeholder="Write a concise professional summary..."
          fullWidth
        />
        {updating && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Saving...</Typography>
          </Box>
        )}
      </Stack>
    ) : (
      <Box>
        {resume ? (<Stack spacing={0}>

  {/* Edit button at top right */}

  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
    {resume}
  </Typography>

</Stack>

        ) : (
          <Typography variant="body1" color="text.secondary">
            No resume summary added yet. {isOwnProfile && "Click edit to add a professional summary."}
          </Typography>
        )}
      </Box>
    )}
  </Box>


  <Divider sx={{ py: 1 }} />

{/* Education */}
  <Box sx={{  pt: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="h3">Education</Typography>
      {isOwnProfile && (
        <Button 
          startIcon={<AddIcon />} 
          variant="outlined" 
          size="small"
          onClick={() => {
            setCurrentEditItem(null);
            setEducationDialogOpen(true);
          }}
        >
          Add Education
        </Button>
      )}
    </Box>

    {loadingEducation ? (
      <CircularProgress size={24} sx={{ mt: 2 }} />
    ) : (
      <Box>
        {education.length > 0 ? (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {education.map((edu) => (
              <Paper key={edu.id} sx={{ p: 1.5, position: 'relative', 
                background: bgtheme.backgroundColor, borderRadius: 1, border: 1, 
                borderColor: theme.palette.grey[50], boxShadow: theme.shadows[1] 

               }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2,  }}>
                  <SchoolIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1, }}>
                    <Typography variant="body1" fontWeight="bold">
                      {edu.degree} in {edu.field_of_study}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {edu.institution}

                    </Typography>
                 

                    
                      {(edu.start_date || edu.end_date) && (
                      <Typography variant="body1" color="text.secondary">
                        {edu.start_date && new Date(edu.start_date).getFullYear()}
                        {edu.start_date && edu.end_date && ' - '}
                        {edu.is_current ? ' - Present' : edu.end_date && new Date(edu.end_date).getFullYear()}
                      </Typography>
                    )}


                    {edu.description && (
                      <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                        {edu.description}
                      </Typography>
                    )}
                    
                  </Box>
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                    <ReflectButton
                      count={edu.reflections_count}
                      onReflectHandle={() => handleReflect('education', edu.id)}
                      isOwnProfile={isOwnProfile}
                    />
                    {/* Edit/Delete buttons - only for owner */}
                    {isOwnProfile && (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={() => openEditEducationDialog(edu)}
                          title="Edit education"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteEducation(edu.id)}
                          title="Delete education"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            No education information added yet. {isOwnProfile && "Education entries will appear here."}
          </Typography>
        )}
      </Box>
    )}
  </Box>

  <Divider sx={{ py: 2 }} />


{/* Work Experience */}
  <Box sx={{  pt: 2, pb: 3 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
      <Typography variant="h3">Work Experience</Typography>
      {isOwnProfile && (
        <Button 
          startIcon={<AddIcon />} 
          variant="outlined" 
          size="small"
          onClick={() => {
            setCurrentEditItem(null);
            setWorkExperienceDialogOpen(true);
          }}
        >
          Add Experience
        </Button>
      )}
    </Box>




    {loadingExperience ? (
      <CircularProgress size={24} sx={{ mt: 2 }} />
    ) : (
      <Box>
        {workExperience.length > 0 ? (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {workExperience.map((exp) => (
              <Paper key={exp.id} sx={{ p: 1.5, position: 'relative', 
              background: bgtheme.backgroundColor, borderRadius: 1, border: 1, 
              borderColor: theme.palette.grey[50], boxShadow: theme.shadows[1] }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <WorkIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {exp.position}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {exp.organization}
                      {exp.location && ` • ${exp.location}`}
                    </Typography>
                    {(exp.start_date || exp.end_date) && (
                      <Typography variant="body1" color="text.secondary">
                        {exp.start_date && new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        {exp.start_date && exp.end_date && ' - '}
                        {exp.is_current ? ' - Present' : exp.end_date && new Date(exp.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </Typography>
                    )}
                    {exp.description && (
                      <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                        {exp.description}
                      </Typography>
                    )}
                    
                    </Box>

<Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                    <ReflectButton
                          count={exp.reflections_count}
                          onReflectHandle={() => handleReflect('workexperience', exp.id)}
                          isOwnProfile={isOwnProfile}
                    />

                    {/* Edit/Delete buttons - only for owner */}
                    {isOwnProfile && (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={() => openEditWorkExperienceDialog(exp)}
                          title="Edit work experience"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteWorkExperience(exp.id)}
                          title="Delete work experience"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
             </Box>
             </Box>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            No work experience added yet. {isOwnProfile && "Work experience entries will appear here."}
          </Typography>
        )}
      </Box>
    )}
  </Box>


  <Divider sx={{ py: 2 }} />

{/* Networks */}
  <Box sx={{ pt: 2, pb: 5}}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="h3">Networks</Typography>
      {isOwnProfile && (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => { setCurrentEditNetwork(null); setNetworkDialogOpen(true); }}
        >
          Add Network
        </Button>
      )}
    </Box>

    {loadingNetworks ? (
      <CircularProgress size={24} sx={{ mt: 2 }} />
    ) : networks.length > 0 ? (
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {networks.map((net) => (
          <Paper key={net.id} sx={{
            p: 1.5, position: 'relative',
            background: bgtheme.backgroundColor, borderRadius: 1,
            border: 1, borderColor: theme.palette.grey[50], boxShadow: theme.shadows[1],
          }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <GroupsIcon color="primary" sx={{ mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold">{net.name}</Typography>
                {net.domain && (
                  <Typography variant="body2" color="text.secondary">{net.domain}</Typography>
                )}
                {net.role && (
                  <Typography variant="body2" color="text.secondary">{net.role}</Typography>
                )}
                {net.description && (
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{net.description}</Typography>
                )}
                {net.website && (
                  <Typography variant="body2">
                    <a href={net.website} target="_blank" rel="noopener noreferrer">{net.website}</a>
                  </Typography>
                )}
              </Box>
              {isOwnProfile && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => { setCurrentEditNetwork(net); setNetworkDialogOpen(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteNetwork(net.id)} title="Remove network">
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Stack>
    ) : (
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        No networks added yet. {isOwnProfile && "Add research or professional networks you belong to."}
      </Typography>
    )}
  </Box>


</Stack>

 

            )}



            {/* Publications */}
            {activeTabKey === 'publications' && (
              <Box sx={{ p: 3, minHeight: '500px' }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3">Publications</Typography>
                    {publications.length > 0 && (
                      <Chip 
                        label={`${publications.reduce((sum, pub) => sum + (pub.citation_count || 0), 0)} total citations`}
                        color="primary"
                        variant="outlined"
                        size="medium"
                      />
                    )}
                  </Box>
                  {isOwnProfile && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* {publications.length > 0 && (
                        <Button
                          startIcon={updatingCitations ? <CircularProgress size={16} /> : <TrendingUpIcon />}
                          variant="outlined"
                          size="small"
                          onClick={handleUpdateCitations}
                          disabled={updatingCitations}
                          color="secondary"
                        >
                          Update Citations
                        </Button>
                      )} */}
                 


                 <Tooltip title="Import Multiple Publications">
                                  <IconButton  onClick={() => setBulkImportOpen(true)}>
                                    <ImportIcon  color="primary" />
                                  </IconButton>
                      </Tooltip>

                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setCurrentEditItem(null);
                          setPublicationDialogOpen(true);
                        }}
                      >
                        Add Publication
                      </Button>

                
               



                    </Box>
                  )}
                </Box>

                {loadingPublications ? (
                  <CircularProgress size={24} sx={{ mt: 2 }} />
                ) : (
                  <Box>
                    {publications.length > 0 ? (
                      <DragDropContext onDragEnd={handleDragEndPublication}>
                        <Droppable droppableId="publications-list" isDropDisabled={!isOwnProfile}>
                          {(provided, snapshot) => (
                            <Stack 
                              spacing={1.5} 
                              sx={{ mt: 2 }}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {publications.map((pub, index) => (
                                <Draggable 
                                  key={pub.id} 
                                  draggableId={pub.id} 
                                  index={index}
                                  isDragDisabled={!isOwnProfile}
                                >
                                  {(provided, snapshot) => (
                                    <Paper 
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      sx={{ 
                                        p: 1.5, 
                                        position: 'relative', 
                                        display: 'flex', 
                                        gap: 1,
                                        // border: 1,
                                        // borderColor: 'divider',
                                        background: snapshot.isDragging ? theme.palette.grey[50] : bgtheme.backgroundColor,
                                              borderRadius: 1, 
                                              borderColor: theme.palette.grey[50]
                                        
                                        ,
                                        boxShadow: snapshot.isDragging ? 6 : 1,
                                      }}
                                    >
                                      {isOwnProfile && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5, alignItems: 'center' }}>
                                          <IconButton
                                            size="small"
                                            {...provided.dragHandleProps}
                                            sx={{ 
                                              p: 0.5,
                                              cursor: 'grab',
                                              '&:active': { cursor: 'grabbing' }
                                            }}
                                          >
                                            <DragIcon fontSize="small" />
                                          </IconButton>
                                          <Divider sx={{ width: '100%', my: 0.5 }} />
                                          <IconButton
                                            size="small"
                                            onClick={() => handleReorderPublication(pub.id, 'up')}
                                            disabled={index === 0}
                                            sx={{ 
                                              p: 0.5,
                                              '&:disabled': { opacity: 0.3 }
                                            }}
                                          >
                                            <ArrowUpIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleReorderPublication(pub.id, 'down')}
                                            disabled={index === publications.length - 1}
                                            sx={{ 
                                              p: 0.5,
                                              '&:disabled': { opacity: 0.3 }
                                            }}
                                          >
                                            <ArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                      <Box sx={{ flex: 1, pr: 12 }}>
                                        <Typography variant="body1" fontWeight={600}>
                                          {pub.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {pub.authors}
                                        </Typography>
                                        {pub.journal && (
                                          <Typography variant="body2" color="text.secondary">
                                            {pub.journal}
                                            {pub.volume && `, Vol. ${pub.volume}`}
                                            {pub.issue && ` (${pub.issue})`}
                                            {pub.year && `, ${pub.year}`}
                                          </Typography>
                                        )}
                                        {pub.doi && (
                                          <Typography variant="caption" color="primary">
                                            DOI: {pub.doi}
                                          </Typography>
                                        )}
                                        {pub.citation_count > 0 && (
                                          <Chip 
                                            label={`${pub.citation_count} citations`} 
                                            size="small" 
                                            sx={{  ml:1 }} 
                                          />
                                        )}
                                        
                                      </Box>
                                        
                                      <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                        <ReflectButton
                                          count={pub.reflections_count}
                                          onReflectHandle={() => handleReflect('publication', pub.id)}
                                          isOwnProfile={isOwnProfile}
                                        />
                                        {/* Edit/Delete buttons - only for owner */}
                                        {isOwnProfile && (
                                          <>
                                            <IconButton 
                                              size="small" 
                                              onClick={() => openEditPublicationDialog(pub)}
                                              title="Edit publication"
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                              size="small" 
                                              onClick={() => handleDeletePublication(pub.id)}
                                              title="Delete publication"
                                            >
                                              <CancelIcon fontSize="small" />
                                            </IconButton>
                                          </>
                                        )}
                                      </Box>
                                    </Paper>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Stack>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        No publications added yet. {isOwnProfile && "Publication entries will appear here."}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Patents subsection */}
                <Box sx={{ mt: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* <PatentIcon color="primary" /> */}
                      <Typography variant="h3">Patents</Typography>
                      {patents.length > 0 && <Chip label={patents.length} size="small" color="primary" variant="outlined" />}
                    </Box>
                    {isOwnProfile && (
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={() => { setCurrentEditPatent(null); setPatentDialogOpen(true); }}
                      >
                        Add Patent
                      </Button>
                    )}
                  </Box>

                  {loadingPatents ? (
                    <CircularProgress size={24} sx={{ mt: 2 }} />
                  ) : patents.length === 0 ? (
                    <Box sx={{ py: 1,  }}>
                      {/* <PatentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} /> */}
                      <Typography color="text.secondary">
                        No patents added yet.{isOwnProfile && ' Patent entries will appear here.'}
                      </Typography>
                    </Box>
                  ) : (
                    <DragDropContext onDragEnd={handleDragEndPatent}>
                      <Droppable droppableId="patents-list" isDropDisabled={!isOwnProfile}>
                        {(provided) => (
                          <Stack spacing={2} sx={{ mt: 1 }} ref={provided.innerRef} {...provided.droppableProps}>
                            {patents.map((pat, index) => (
                              <Draggable key={pat.id} draggableId={pat.id} index={index} isDragDisabled={!isOwnProfile}>
                                {(provided, snapshot) => (
                                  <Paper
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', gap: 1, boxShadow: snapshot.isDragging ? 6 : 1, background: snapshot.isDragging ? theme.palette.grey[50] : bgtheme.backgroundColor }}
                                  >
                                    {isOwnProfile && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5, alignItems: 'center' }}>
                                        <IconButton size="small" {...provided.dragHandleProps} sx={{ p: 0.5, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                                          <DragIcon fontSize="small" />
                                        </IconButton>
                                        <Divider sx={{ width: '100%', my: 0.5 }} />
                                        <IconButton size="small" disabled={index === 0} sx={{ p: 0.5, '&:disabled': { opacity: 0.3 } }}
                                          onClick={async () => { const r = Array.from(patents); r.splice(index - 1, 0, r.splice(index, 1)[0]); setPatents(r); try { await axiosService.post('/patents/bulk-reorder/', { patent_order: r.map((p, i) => ({ id: p.id, display_order: i + 1 })) }); } catch { await fetchPatents(); } }}>
                                          <ArrowUpIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" disabled={index === patents.length - 1} sx={{ p: 0.5, '&:disabled': { opacity: 0.3 } }}
                                          onClick={async () => { const r = Array.from(patents); r.splice(index + 1, 0, r.splice(index, 1)[0]); setPatents(r); try { await axiosService.post('/patents/bulk-reorder/', { patent_order: r.map((p, i) => ({ id: p.id, display_order: i + 1 })) }); } catch { await fetchPatents(); } }}>
                                          <ArrowDownIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle1" fontWeight={600}>{pat.title}</Typography>
                                            <Chip label={pat.status.charAt(0).toUpperCase() + pat.status.slice(1)} size="small"
                                              color={pat.status === 'granted' ? 'success' : pat.status === 'pending' ? 'warning' : 'default'} variant="outlined" />
                                          </Box>
                                          {pat.patent_number && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>#{pat.patent_number}{pat.country ? ` · ${pat.country}` : ''}</Typography>}
                                          {pat.inventors && <Typography variant="body2" color="text.secondary">Inventors: {pat.inventors}</Typography>}
                                          {(pat.filing_date || pat.grant_date) && (
                                            <Typography variant="caption" color="text.secondary">
                                              {pat.filing_date && `Filed: ${new Date(pat.filing_date).getFullYear()}`}
                                              {pat.filing_date && pat.grant_date && ' · '}
                                              {pat.grant_date && `Granted: ${new Date(pat.grant_date).getFullYear()}`}
                                            </Typography>
                                          )}
                                          {pat.assignee && <Typography variant="caption" color="text.secondary" display="block">Assignee: {pat.assignee}</Typography>}
                                          {pat.abstract && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{pat.abstract}</Typography>}
                                          {pat.url && <Button size="small" href={pat.url} target="_blank" rel="noopener" sx={{ mt: 0.5, textTransform: 'none', p: 0 }}>View patent ↗</Button>}
                                        </Box>
                                        {isOwnProfile && (
                                          <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                            <Tooltip title="Edit">
                                              <IconButton size="small" onClick={() => { setCurrentEditPatent(pat); setPatentDialogOpen(true); }}>
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                              <IconButton size="small" color="error" onClick={() => handleDeletePatent(pat.id)}>
                                                <CancelIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Paper>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Stack>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </Box>
              </Box>
            )}

            {/* Research Stats */}
            {/* {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Typography variant="h6">Research Statistics</Typography>
                  {isOwnProfile && (
                    <IconButton onClick={() => setEditingStats(true)} disabled={updating}><EditIcon /></IconButton>
                  )}
                </Box>

                {editingStats ? (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField label="Publications" type="number" fullWidth
                          value={profileData.publications_count}
                          onChange={(e) => handleInputChange("publications_count", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ publications_count: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving publications count:', err);
                            }
                          }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="Citations" type="number" fullWidth
                          value={profileData.citations_count}
                          onChange={(e) => handleInputChange("citations_count", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ citations_count: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving citations count:', err);
                            }
                          }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="H-Index" type="number" fullWidth
                          value={profileData.h_index}
                          onChange={(e) => handleInputChange("h_index", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ h_index: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving h-index:', err);
                            }
                          }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="Active Projects" type="number" fullWidth
                          value={profileData.active_projects_count}
                          onChange={(e) => handleInputChange("active_projects_count", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ active_projects_count: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving active projects:', err);
                            }
                          }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="Collaborations" type="number" fullWidth
                          value={profileData.collaborations_count}
                          onChange={(e) => handleInputChange("collaborations_count", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ collaborations_count: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving collaborations:', err);
                            }
                          }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="Funding Amount ($)" type="number" fullWidth
                          value={profileData.funding_amount}
                          onChange={(e) => handleInputChange("funding_amount", e.target.value)}
                          onBlur={async (e) => {
                            try {
                              await updateUserProfile({ funding_amount: Number(e.target.value) || 0 });
                            } catch (err) {
                              console.error('Error saving funding amount:', err);
                            }
                          }} />
                      </Grid>
                    </Grid>
                    {updating && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">Saving...</Typography>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <ScienceIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="primary" gutterBottom>{user.publications_count || 0}</Typography>
                        <Typography variant="body2">Publications</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <TrendingUpIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="secondary" gutterBottom>{user.citations_count || 0}</Typography>
                        <Typography variant="body2">Citations</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <StarIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="warning.main" gutterBottom>{user.h_index || 0}</Typography>
                        <Typography variant="body2">H-Index</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <WorkIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="success.main" gutterBottom>{user.active_projects_count || 0}</Typography>
                        <Typography variant="body2">Active Projects</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <GroupIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="info.main" gutterBottom>{user.collaborations_count || 0}</Typography>
                        <Typography variant="body2">Collaborations</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                    <Grid item xs={6}>
                      <StatsCard><CardContent sx={{ textAlign: "center" }}>
                        <TrendingUpIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" color="primary" gutterBottom>{formatCurrency(user.funding_amount)}</Typography>
                        <Typography variant="body2">Total Funding</Typography>
                      </CardContent></StatsCard>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )} */}

            {/* Activity (placeholder; hook in your feed later) */}
            {/* {activeTab === 8 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                <Paper sx={{ p: 2, bgcolor: "grey.50", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <FavoriteIcon color="error" />
                    <Typography variant="subtitle2">Liked Calls</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {user.calls_liked?.length || 0} funding calls liked
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, bgcolor: "grey.50", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="subtitle2">Profile Completeness</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Profile is {calculateProfileCompleteness(user)}% complete
                  </Typography>
                </Paper>
              </Box>
            )} */}

       

            {/* Projects */}
            {activeTabKey === 'projects' && (
              <Box sx={{ p: 3, minHeight: '500px' }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="h3">Research Projects</Typography>
                  {isOwnProfile && (
                    <Button 
                      startIcon={<AddIcon />} 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        setCurrentEditItem(null);
                        setProjectDialogOpen(true);
                      }}
                    >
                      Add Project
                    </Button>
                  )}
                </Box>

                {loadingProjects ? (
                  <CircularProgress size={24} sx={{ mt: 2 }} />
                ) : (
                  <Box>
                    {pastProjects.length > 0 ? (
                      <DragDropContext onDragEnd={handleDragEndProject}>
                        <Droppable droppableId="projects-list" isDropDisabled={!isOwnProfile}>
                          {(provided) => (
                            <Stack spacing={1.5} sx={{ mt: 2 }} ref={provided.innerRef} {...provided.droppableProps}>
                              {pastProjects.map((project, index) => (
                                <Draggable key={project.id} draggableId={project.id} index={index} isDragDisabled={!isOwnProfile}>
                                  {(provided, snapshot) => (
                                    <Paper
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      sx={{ p: 1.5, position: 'relative', border: 1, display: 'flex', gap: 1, background: snapshot.isDragging ? theme.palette.grey[50] : bgtheme.backgroundColor, borderRadius: 1, borderColor: theme.palette.grey[50], boxShadow: snapshot.isDragging ? 6 : theme.shadows[1] }}
                                    >
                                      {isOwnProfile && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5, alignItems: 'center' }}>
                                          <IconButton size="small" {...provided.dragHandleProps} sx={{ p: 0.5, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
                                            <DragIcon fontSize="small" />
                                          </IconButton>
                                          <Divider sx={{ width: '100%', my: 0.5 }} />
                                          <IconButton size="small" disabled={index === 0} sx={{ p: 0.5, '&:disabled': { opacity: 0.3 } }}
                                            onClick={async () => { const r = Array.from(pastProjects); r.splice(index - 1, 0, r.splice(index, 1)[0]); setPastProjects(r); try { await axiosService.post('/projects/bulk-reorder/', { project_order: r.map((p, i) => ({ id: p.id, display_order: i + 1 })) }); } catch { await fetchProjects(); } }}>
                                            <ArrowUpIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton size="small" disabled={index === pastProjects.length - 1} sx={{ p: 0.5, '&:disabled': { opacity: 0.3 } }}
                                            onClick={async () => { const r = Array.from(pastProjects); r.splice(index + 1, 0, r.splice(index, 1)[0]); setPastProjects(r); try { await axiosService.post('/projects/bulk-reorder/', { project_order: r.map((p, i) => ({ id: p.id, display_order: i + 1 })) }); } catch { await fetchProjects(); } }}>
                                            <ArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                      <Box sx={{ flex: 1, pr: 12 }}>
                                        <Typography variant="body1" fontWeight="600">{project.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {project.role}{project.organization && ` • ${project.organization}`}
                                        </Typography>
                                        {(project.start_date || project.end_date) && (
                                          <Typography variant="caption" color="text.secondary">
                                            {project.start_date && new Date(project.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                            {project.start_date && project.end_date && ' - '}
                                            {project.is_current ? 'Present' : project.end_date && new Date(project.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                          </Typography>
                                        )}
                                        {project.funding_source && (
                                          <Typography variant="body2" sx={{ mt: 0.75 }}>
                                            <strong>Funding:</strong> {project.funding_source}
                                            {project.funding_amount && ` - $${Number(project.funding_amount).toLocaleString()}`}
                                          </Typography>
                                        )}
                                        {project.description && <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>{project.description}</Typography>}
                                        <Chip label={project.status} size="small" color={project.status === 'active' ? 'success' : 'default'} sx={{ mt: 0.75 }} />
                                        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                          <ReflectButton count={project.reflections_count} onReflectHandle={() => handleReflect('project', project.id)} isOwnProfile={isOwnProfile} />
                                          {isOwnProfile && (
                                            <>
                                              <IconButton size="small" onClick={() => openEditProjectDialog(project)} title="Edit project"><EditIcon fontSize="small" /></IconButton>
                                              <IconButton size="small" onClick={() => handleDeleteProject(project.id)} title="Delete project"><CancelIcon fontSize="small" /></IconButton>
                                            </>
                                          )}
                                        </Box>
                                      </Box>
                                    </Paper>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Stack>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        No research projects added yet. {isOwnProfile && "Project entries will appear here."}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}




            {/* Education */}
            {/* {activeTab === 4 && (

            )} */}

            {/* Work Experience */}
            {/* {activeTab === 5 && (

            )} */}

        
            {/* Endorsements */}
            {/* {activeTab === 7 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Typography variant="h6">Endorsements</Typography>
                  {isOwnProfile && (
                    <Button variant="outlined" startIcon={<AddIcon />} size="small">
                      Request Endorsement
                    </Button>
                  )}
                </Box>

                <Box>
                  {endorsements.length > 0 ? (
                    <Stack spacing={2}>
                      {endorsements.map((endorsement, index) => (
                        <Paper key={index} sx={{ p: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                            <ThumbUpIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" sx={{ mb: 1 }}>
                                "{endorsement.text}"
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                - {endorsement.author}, {endorsement.position}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {endorsement.date}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No endorsements yet. {isOwnProfile && "Request endorsements from colleagues and collaborators."}
                    </Typography>
                  )}
                </Box>
              </Box> 
            )} */}

            {activeTabKey === 'reflections' && <ReflectionsTab profileId={user?.id} />}

            {/* Stats: everything for the owner, the shared cards for everyone else */}
            {activeTabKey === 'stats' && (
              <MyStatsTab isOwn={isOwnProfile} userId={user?.id} firstName={user?.first_name}
                onCompleteProfile={isOwnProfile ? handleEditProfile : undefined}
                profileUrl={`${window.location.origin}/profile/${user?.id}/`} />
            )}

          </Paper>
        </Grid>
      </Grid>

      {/* Mobile FAB */}
      {isOwnProfile && !editingProfile && !editingStats && !editingPublications && !editingProjects && !editingEducation && !editingExperience && !editingResume && (
        <Fab color="primary" aria-label="edit"
          sx={{ position: "fixed", bottom: 16, right: 16, display: { xs: "flex", md: "none" } }}
          onClick={handleEditProfile} disabled={updating}>
          <EditIcon />
        </Fab>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration ?? 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialogs */}
      <EducationDialog 
        open={educationDialogOpen}
        onClose={() => {
          setEducationDialogOpen(false);
          setCurrentEditItem(null);
        }}
        onSave={handleSaveEducation}
        education={currentEditItem}
        loading={updating}
      />

      <WorkExperienceDialog 
        open={workExperienceDialogOpen}
        onClose={() => {
          setWorkExperienceDialogOpen(false);
          setCurrentEditItem(null);
        }}
        onSave={handleSaveWorkExperience}
        experience={currentEditItem}
        loading={updating}
      />

      <PublicationDialog
        open={publicationDialogOpen}
        onClose={() => {
          setPublicationDialogOpen(false);
          setCurrentEditItem(null);
        }}
        onSave={handleSavePublication}
        publication={currentEditItem}
        loading={updating}
      />

      <BulkImportPublicationsDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        existingDois={publications.map(p => p.doi).filter(Boolean)}
        onImportComplete={async () => {
          await fetchPublications();
          setSnackbar({ open: true, message: 'Publications imported successfully', severity: 'success' });
        }}
      />

      <ProjectDialog
        open={projectDialogOpen}
        onClose={() => {
          setProjectDialogOpen(false);
          setCurrentEditItem(null);
        }}
        onSave={handleSaveProject}
        project={currentEditItem}
        loading={updating}
      />

      <PatentDialog
        open={patentDialogOpen}
        onClose={() => { setPatentDialogOpen(false); setCurrentEditPatent(null); }}
        onSave={handleSavePatent}
        patent={currentEditPatent}
        loading={savingPatent}
      />

      {/* Reflection Dialog */}
      <AddReflectionDialog
        open={reflectionDialogOpen}
        onClose={handleCloseReflectionDialog}
        contentType={reflectionContext.contentType}
        objectId={reflectionContext.objectId}
      />

      {/* Add Affiliation Dialog */}
    <AddAffiliationDialog
        open={addAffiliationDialogOpen}
        onClose={closeAddAffiliationDialog}
        onSubmit={handleAddAffiliation}
        countries={countries}
        allOrganizations={allOrganizations}
        loadingAllOrgs={loadingAllOrgs}
        fetchOrgDepartments={fetchOrgDepartments}
        orgDepartments={orgDepartments}
        loadingDepartments={loadingDepartments}
        submitting={updating}
        isFirstAffiliation={myOrganizations.length === 0}
    /> 

      {/* Edit Affiliation Dialog */}
      <Dialog 
        open={editAffiliationDialogOpen} 
        onClose={closeEditAffiliationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organization Affiliation</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Organization"
              value={affiliationForm.organization?.name || ''}
              disabled
              fullWidth
              helperText="Organization cannot be changed. Remove and add a new affiliation if needed."
            />

            <Autocomplete
              options={orgDepartments}
              freeSolo
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.name || '';
              }}
              value={affiliationForm.department}
              onChange={(_, newValue) => {
                setAffiliationForm(prev => ({ ...prev, department: newValue }));
              }}
              onInputChange={(_, newInputValue) => {
                if (newInputValue && typeof newInputValue === 'string') {
                  setAffiliationForm(prev => ({ ...prev, department: newInputValue }));
                }
              }}
              disabled={!affiliationForm.organization || loadingDepartments}
              loading={loadingDepartments}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department (Optional)"
                  placeholder="Select or type a department name"
                  helperText="Type to create a new department"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDepartments ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              freeSolo
              options={ORGANIZATION_ROLES}
              value={affiliationForm.role}
              onChange={(_, newValue) => {
                setAffiliationForm(prev => ({ ...prev, role: newValue || '' }));
              }}
              onInputChange={(_, newInputValue) => {
                // Update role as user types
                setAffiliationForm(prev => ({ ...prev, role: newInputValue }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Role/Position"
                  placeholder="Select or type your role"
                  required
                  helperText="Select from list or type a custom role"
                />
              )}
            />

            <TextField
              label="Start Date"
              type="date"
              value={affiliationForm.start_date}
              onChange={(e) => setAffiliationForm(prev => ({ ...prev, start_date: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Status"
              value={affiliationForm.status}
              onChange={(e) => setAffiliationForm(prev => ({ ...prev, status: e.target.value }))}
              fullWidth
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </TextField>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="edit-is-primary-checkbox"
                checked={affiliationForm.is_primary}
                onChange={(e) => setAffiliationForm(prev => ({ ...prev, is_primary: e.target.checked }))}
              />
              <label htmlFor="edit-is-primary-checkbox">
                <Typography variant="body2">Set as primary organization</Typography>
              </label>
            </Box>

            <Alert severity="info">
              Update your affiliation details. Organization cannot be changed - remove and add a new one if needed.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditAffiliationDialog} disabled={updating}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditAffiliation} 
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <EditIcon />}
          >
            Update Affiliation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Membership Dialog */}
      <VerifyMembershipDialog
        open={verifyDialogOpen}
        onClose={closeVerifyDialog}
        membership={membershipToVerify}
        onUploadDocument={handleUploadVerificationDocument}
        onVerified={() => {
          mutateMyOrgs();
          setSnackbar({
            open: true,
            severity: 'success',
            duration: 12000,
            message: '🎉 Membership verified! You\'ve received 2 free AI credits — open the Proposal Writer and try the AI writing assistant on your next proposal.',
          });
        }}
      />

      {/* Meeting Schedule Dialog */}
      {isOwnProfile && (
        <>
          <CVImportDialog
            open={cvImportOpen}
            onClose={() => setCvImportOpen(false)}
            currentValues={{ ...user, bio: user.bio, resume }}
            onApplied={handleCVApplied}
          />
          <CVImportDialog
            mode="public"
            open={publicImportOpen}
            onClose={() => setPublicImportOpen(false)}
            currentValues={{ ...user, bio: user.bio, resume }}
            onApplied={handleCVApplied}
          />
          <AIIdeasDialog
            open={ideasOpen}
            onClose={() => setIdeasOpen(false)}
            onImportCV={() => { setIdeasOpen(false); setCvImportOpen(true); }}
            onEditProfile={() => { setIdeasOpen(false); handleEditProfile(); }}
          />
        </>
      )}

      <MeetingScheduleDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        restrictToUsers={[user]}
      />

      {/* Invite to Consortium Dialog */}
      <InviteToConsortiumDialog
        open={inviteToConsortiumDialogOpen}
        onClose={() => setInviteToConsortiumDialogOpen(false)}
        user={user}
      />

      {/* Suggest Entity Dialog */}
      <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        user={user}
      />

      {/* Network Dialog */}
      <NetworkDialog
        open={networkDialogOpen}
        network={currentEditNetwork}
        onClose={() => { setNetworkDialogOpen(false); setCurrentEditNetwork(null); }}
        onSave={handleSaveNetwork}
        saving={updating}
      />
      {guide.open && (
        <GuideTour steps={PROFILE_GUIDE_STEPS} step={guide.step} targetRect={guide.targetRect}
          onNext={guide.next} onClose={guide.close} ariaLabel="Profile guide" />
      )}
    </PageContainer>
  );
};

/* ===== NetworkDialog ===== */
const EMPTY_NETWORK = { name: '', domain: '', description: '', website: '', role: '' };

function NetworkDialog({ open, network, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_NETWORK);

  useEffect(() => {
    setForm(network ? {
      id: network.id,
      name: network.name || '',
      domain: network.domain || '',
      description: network.description || '',
      website: network.website || '',
      role: network.role || '',
    } : EMPTY_NETWORK);
  }, [network, open]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{network ? 'Edit Network' : 'Add Network'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField label="Network Name *" value={form.name} onChange={set('name')} fullWidth size="small" />
          <TextField label="Domain / Field" value={form.domain} onChange={set('domain')} fullWidth size="small"
            helperText="e.g., Computational Biology, Climate Science" />
          <TextField label="Your Role" value={form.role} onChange={set('role')} fullWidth size="small"
            helperText="e.g., Member, Chair, Coordinator" />
          <RichTextField label="Description" value={form.description}
            onChange={(val) => setForm(f => ({ ...f, description: val }))} height={120} />
          <TextField label="Website" value={form.website} onChange={set('website')} fullWidth size="small"
            placeholder="https://" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.name || saving} onClick={() => onSave(form)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProfilePage;
